import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import {
  ABGEMELDET,
  api,
  Fachbefund,
  imHintergrund,
  Rechenabbruch,
  setzeAnzeigewaehrung,
  setzeBerichtsumrechnung,
  still,
} from './api'
import { fachlicherSatz } from './Fehlersatz'
import { erlaubt, Rechtekontext, useDarf, useRechtelage } from './Rechte'
import type { Handgriffname } from './Handgriffe'
import { meldeErfolg } from './Erfolgsstelle'
import { Anzeigewaehrungswahl, wirksameAnzeigewaehrung } from './Anzeigewaehrung'
import { Bericht, positionAus, stelleAus, useAufriss } from './Bericht'
import type { Zellenzeiger } from './Bericht'
import { Lieferstand } from './Lieferstand'
import { BerichtNachPerioden, BerichtNachStruktur, einheitenIm, reihe } from './BerichtStruktur'
import { ausGliederung } from './Gliederungsbericht'
import { Spiegel } from './Spiegel'
import {
  Kapitalflussrechnung,
  Kursanpassungsschalter,
  KapitalflussNachPerioden,
  einheitenIn,
} from './Kapitalflussrechnung'
import type { Kfrzellenzeiger } from './Kapitalflussrechnung'
import { Eigenschaftsfenster } from './Eigenschaftsfenster'
import type { Eigenschaftsziel } from './Eigenschaftsfenster'
import { Positionseinordnung } from './Positionseinordnung'
import { Reihenfolgefenster } from './Reihenfolgefenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'
import { KapitalflussNachStruktur } from './KapitalflussStruktur'
import { merkeAchsen, weitereVon } from './Aufriss'
import type { Zelle } from './Aufriss'
import type { Achsenquelle } from './Aufrisszeilen'
import { Kfrwertenachweis } from './Kfrwertenachweis'
import type { Kfrnachweisziel } from './Kfrwertenachweis'
import { Auswahlfeld, ausStammdatum, mitCode } from './Auswahlfeld'
import { Suchfeld } from './Suchfeld'
import { suchen, suchgutBericht, suchgutPerioden } from './Suche'
import { Spaltenwahl } from './Spaltenwahl'
import type { Spaltenmodus } from './Spaltenwahl'
import { Zeitschieber } from './Zeitschieber'
import { einheitenname, einheitVon, runde, stichtag, vorbelegung } from './Zeitraum'
import type { Raster } from './Zeitraum'
import { alsZeitraum, anfangszeitraum, liesWahl, merkeWahl, neuereZahlen, useGemerkt } from './Gemerkt'
import type { Zeitraumwahl } from './Gemerkt'
import { Buchungsjournal } from './Buchungsjournal'
import { Belegordner } from './Belegordner'
import { Belegmaske } from './Belegmaske'
import type { Buchungsvorbelegung } from './Belegmaske'
import { Strukturanlage } from './Strukturanlage'
import type { Anlageziel } from './Strukturanlage'
import { Wertenachweis } from './Wertenachweis'
import type { Nachweisziel } from './Wertenachweis'
import { Seitenleiste } from './Seitenleiste'
import { Profil } from './Profil'
import { Erfolgsmoment } from './Erfolgsmoment'
import { Seriezeile } from './Seriezeile'
import { Lieferung } from './Datenimport'
import { belegzielLesen, rueckweg } from './Belegsprung'
import { Aktualisierungswarnung } from './Aktualisierungswarnung'
import { Schloss } from './Schloss'
import { Fassungshinweis } from './Fassungshinweis'
import { Datenquellen } from './Datenquelle'
import { Packagevorlagen } from './Packagevorlagen'
import { Kontenzuordnung } from './Kontenzuordnung'
import { Hilfe } from './Hilfe'
import { Begleiter } from './Begleiter'
import { schreibeStand as begleitlisteSchreiben, sprungEmpfangen } from './Begleitliste'
import { Anhangsbuch, Angabenkatalog, Vollstaendigkeit } from './Anhang'
import { Konzernhandbuch } from './Konzernhandbuch'
import { Konsolidierungsschritte } from './Konsolidierungsschritte'
import { Kreismitglieder } from './Kreismitglieder'
import { Lieferrhythmen } from './Lieferrhythmen'

import { Konzernabschluesse } from './Konzernabschluesse'
import { Weltkopf, Weltzeile } from './Weltzeile'
import { Stammdaten, STAMMDATENARTEN } from './Stammdaten'
import { Ledgeraufbau } from './Ledgeraufbau'
import { Partnercodes } from './Partnercodes'
import { Stammdatenaustausch } from './Stammdatenaustausch'
import { Wechselkurse } from './Wechselkurse'
import { HistorischeKurse } from './HistorischeKurse'
import { Perioden } from './Perioden'
import { Positionsgliederung } from './Positionsgliederung'
import { Einfuegefenster } from './Einfuegefenster'
import { Elementloeschfenster } from './Elementloeschung'
import { beobachteSpeichern } from './Nachladen'
import { Kontogruppe } from './Kontogruppe'
import { Platzhalter } from './Platzhalter'
import { Prueferansicht } from './Prueferansicht'
import { Abbruchweg } from './Abbruchverweise'
import { anwendungszeilen, offenePunkte, Vorrat } from './Arbeitsliste'
import { offeneFaelle, Ueberwachungsblatt, type Blattsprung, type Ueberwachungsstand } from './Uebersichtsblatt'
import { befundtexte } from './Meldungen'
import type { Meldungskontext } from './Meldungen'
import { Verweissatz } from './Verweissatz'
import { Kachelbrett } from './Kachelbrett'
import { Uebergangene, Ungeprueftvermerk, verlangtSatz } from './Pruefregeln'
import { Abschlusstafel } from './Kachelinhalt'
import { Anwenderliste } from './Anwender'
import { Anmeldemaske, Kennwortmaske } from './Anmeldung'
import { ROLLE_KONZERNBUCHHALTER, ersteKachel } from './Kacheln'
import {
  bereiche,
  bereichsname,
  brauchtKontext,
  findeBereich,
  istTafel,
  unterpunktname,
} from './bereiche'
import { Sprachen } from './Sprachen'
import { useTabellenrahmen } from './Tabellenrahmen'
import { Schnellzugriff } from './Schnellzugriff'
import { eigeneZahlendarstellung } from './Zahlenwahl'
import type { Zahlenstand } from './Zahlenwahl'
import { useZellmarkierung, Zellsumme } from './Zellsumme'
import { Anzeigeoptionenknopf, Anzeigeoptionenkontext } from './Anzeigeoptionen'
import { Gliederungskontext, vorfahren } from './Gliederung'
import { Zeitwertwahl, ZEITWERTVORGABE } from './Zeitwert'
import type { Zeitwert } from './Zeitwert'
import {
  abschlussstatus,
  aktuelleSprache,
  aktuellesZahlenformat,
  aktuelleMinusfarbe,
  aktuelleVorzeichenart,
  anzahlwort,
  beschriftung,
  bewertungsebenenname,
  katalog,
  katalogFehlt,
  katalogLaedt,
  kreisname as kreisnameImKatalog,
  periodenname,
  satz,
  setzeKatalog,
  setzePartnercodes,
  setzeSprache,
  setzeMinusfarbe,
  setzeVorzeichenart,
  setzeZahlenformat,
  t,
  zeitpunkt,
} from './sprache'
import type { Minusfarbe, Sprachschluessel, Vorzeichenart, Zahlenformat } from './sprache'
import type {
  Datenquelle,
  Achsenangabe,
  Aufrisszelle,
  Auswahl,
  Abschluss,
  Angemeldet,
  Bericht as Berichtssatz,
  Gliederungsbild,
  Kfrbeitrag,
  Kfrperiodenaufriss,
  Kfrzusatzzelle,
  Periodenaufriss,
  Spiegelaufriss,
  Zusatzzelle,
  Abschlusszeile,
  Freigabebefund,
  Freigabeurteil,
  Messwerte,
  Nachweispruefung,
  Spiegel as Spiegelsatz,
  Kapitalflussrechnung as Kfrsatz,
  Kfrperiodenbericht,
  Periodenbericht,
  Positionsplan,
  Rechenantwort,
  Textbaustein,
  Vorgangskopf,
  Anzeigewaehrungen,
  Berichtswelt,
} from './typen'

/**
 * Die Schale: Seitenleiste links, Kontextzeile oben, Arbeitsfeld dazwischen
 * (7.2, R149/R150/R153).
 *
 * Die Startseite ist die Arbeitsliste — die Leiste ist der zweite Weg, nicht
 * der erste (R140/R149). Wer sich anmeldet, sieht seinen Arbeitsvorrat; wer
 * ausdrücklich woandershin will, nimmt die Leiste.
 *
 * Der waagerechte Streifen oben trägt Konsolidierungskreis, Bewertungsebene und Periode
 * — den Kontext, der auf jedem Bildschirm gilt. Genau dafür ist er frei
 * gehalten worden, indem die Navigation senkrecht steht.
 */

/**
 * `ziel` trägt, worauf ein Link zeigt — die Zeile, nicht nur die Seite.
 *
 * Der Wertenachweis verlinkt an **die Stelle** im Buchungsjournal (2.14,
 * R209): Beleg geöffnet, Zeile hervorgehoben. Ohne einen Platz in der Adresse
 * ginge das beim Neuladen verloren, und ein Link, den man nicht weitergeben
 * kann, ist keiner (R156).
 */
type Ort = { bereich: string; unterpunkt: string | null; ziel: Record<string, string> }

/**
 * Jeder Ort hat genau eine Adresse und ist tief verlinkbar (R156).
 *
 * Die Adresse steht im Fragment, nicht im Pfad: Der Prototyp wird von der API
 * ausgeliefert, und ein Pfad-Router bräuchte dort eine Rückfallregel für jede
 * unbekannte URL. Das ist eine Betriebsfrage, keine fachliche — die Regel
 * „eine Adresse je Ort" ist so oder so erfüllt.
 */
function leseAdresse(): Ort {
  const roh = window.location.hash.replace(/^#\/?/, '')
  const [pfad = '', frage] = roh.split('?')
  const gelesen = new URLSearchParams(frage ?? '')
  // Der mitgegebene Kontext gehört nicht ins Ziel: Er wirkt einmal beim Start
  // und ist danach kein Teil dessen, was der Ort über sich sagt (R530).
  for (const feld of MITGEGEBEN) {
    gelesen.delete(feld)
  }
  const ziel = Object.fromEntries(gelesen)
  const teile = pfad.split('/').filter(Boolean)

  if (teile.length === 0) {
    return { bereich: 'arbeitsliste', unterpunkt: null, ziel }
  }

  const bereich = bereiche.find((b) => b.id === teile[0])
  if (!bereich) {
    return { bereich: 'arbeitsliste', unterpunkt: null, ziel }
  }

  const unterpunkt = bereich.unterpunkte.find((u) => u.id === teile[1])?.id ?? null
  return {
    bereich: bereich.id,
    unterpunkt: unterpunkt ?? bereich.unterpunkte[0]?.id ?? null,
    ziel,
  }
}

/**
 * Die Felder, die ein **zweites Fenster beim Öffnen mitbekommt** (7.2
 * Abschnitt 3c, R530): Kreis, Bewertungsebene und der Zeitraum.
 *
 * Sie sind kein Teil des Ortes. Die Adresse eines Ortes sagt, *wo* man ist —
 * diese fünf sagen, *womit* das Fenster anfangen soll, und danach nie wieder
 * etwas. Deshalb liest die Schale sie **einmal** und nimmt sie dann aus der
 * Adresse: Ein Feld, das stehen bliebe, behauptete eine Kopplung, die es nicht
 * gibt — wer im zweiten Fenster den Kreis wechselt, hätte eine Adresse, die
 * etwas anderes sagt als der Bildschirm darunter.
 */
const MITGEGEBEN = ['k', 'l', 'v', 'b', 'r'] as const

/** Was dieses Fenster beim Öffnen mitbekommen hat — einmal gelesen, dann fest. */
const KONTEXT_BEIM_OEFFNEN: Record<string, string> = (() => {
  const frage = new URLSearchParams(window.location.hash.replace(/^#\/?/, '').split('?')[1] ?? '')
  return Object.fromEntries(
    MITGEGEBEN.map((feld) => [feld, frage.get(feld) ?? '']).filter(([, wert]) => wert !== ''),
  )
})()

/** Der Zeitraum, den ein zweites Fenster mitbekommen hat (R530). */
const ZEITRAUM_MITGEGEBEN =
  KONTEXT_BEIM_OEFFNEN.v && KONTEXT_BEIM_OEFFNEN.b
    ? alsZeitraum({
        von: Number(KONTEXT_BEIM_OEFFNEN.v),
        bis: Number(KONTEXT_BEIM_OEFFNEN.b),
        raster: KONTEXT_BEIM_OEFFNEN.r,
      })
    : null

/**
 * **Der gemerkte Zeitraum, einmal beim Öffnen gelesen** (R1363) — bevor der
 * Bildschirm seine Zwischenstände schreibt. Danach gilt, was steht.
 */
const ZEITRAUM_GEMERKT = 'kontext.zeitraum'
const ZEITRAUM_BEIM_OEFFNEN = alsZeitraum(liesWahl(ZEITRAUM_GEMERKT))

const LEISTE_GEMERKT = 'coko.leiste'

/** Ersatz, wenn die Zusatzachsen im Hintergrund nicht kamen (R1212): kein Angebot. */
const KEINE_ACHSEN: Achsenangabe[] = []

export function App() {
  // Auf einem Blatt rollt nur die Seite (R915) — einmal hier, für jeden Bildschirm.
  useTabellenrahmen()

  const [auswahl, setAuswahl] = useState<Auswahl | null>(null)
  // Kommt das Namensverzeichnis, baut sich der Bildschirm einmal neu auf (R1661).
  const [, setKatalogstand] = useState(0)

  /*
    **Wer angemeldet ist, sagt die API** (R351) — die Oberfläche fragt und
    entscheidet nie selbst. Solange Bereich 8 keine Anmeldung hat, antwortet
    sie mit einem Platzhalter und sagt das im Ergebnis; die Oberfläche gibt
    diesen Satz weiter, statt eine Anmeldung vorzutäuschen.

    Bis die Antwort da ist, gilt der Konzernbuchhalter: Das ist die Vorgabe
    der API, und ein Brett, das erst nach dem ersten Ruf erscheint, flackert.
  */
  const [angemeldet, setAngemeldet] = useState<Angemeldet | null>(null)

  /*
    **Die Rechte und die Liste des Servers, einmal gehalten** (8.1 Abschnitt 7,
    R1180, R1181): Die Anwendung gibt sie an den Rahmen weiter und fragt selbst
    damit, wenn sie Übergaben weglässt — *Buchen* und *Anlegen* an den
    Berichten.
  */
  const rechtelage = useRechtelage(angemeldet?.rechte ?? null)
  const darf = (name: Handgriffname) => erlaubt(rechtelage, name)
  // „Mein Profil" geht am eigenen Namen im Fuß der Seitenleiste auf (7.7, R920).
  const [profilOffen, setProfilOffen] = useState(false)
  /** Erst wenn die API einmal geantwortet hat, wissen wir, ob jemand da ist. */
  const [gefragt, setGefragt] = useState(false)
  /**
   * **Kam keine Antwort, ist niemand abgemeldet** (7.2 Abschnitt 39, R1210):
   * „Nicht angemeldet" sagt die API mit `null`. Ein Ausfall zeigte bis zum
   * 2026-10-06 trotzdem die Anmeldung — als wäre die Sitzung abgelaufen.
   */
  const [ichAusgefallen, setIchAusgefallen] = useState(false)
  const rolle = angemeldet?.rolle ?? ROLLE_KONZERNBUCHHALTER

  /*
    **Eine abgelaufene Sitzung ist kein Fehler, sondern ein Zustand** (R355).
    Jeder Ruf kann sie feststellen; antworten muss nur eine Stelle — diese.
    Der Anwender landet auf der Anmeldung und nicht in einer Störungsmeldung.
  */
  useEffect(() => {
    const abgemeldet = () => setAngemeldet(null)
    window.addEventListener(ABGEMELDET, abgemeldet)
    return () => window.removeEventListener(ABGEMELDET, abgemeldet)
  }, [])

  /**
   * Die Abschlüsse — nur für die erste Kachel des Prüfers geholt (R349). Ein
   * Hauptabruf (7.2 Abschnitt 39, R1211): Kamen sie nicht, steht auf der
   * Kachel „Ließ sich nicht laden" und nicht, es gebe keinen (R1210).
   */
  const abschlussabruf = useAbruf<Abschluss[]>(
    ersteKachel(rolle) === 'abschluesse' ? () => api.abschluesse() : null,
    [rolle],
  )

  const frageWerIchBin = useCallback(() => {
    api
      .ich()
      .then((a) => {
        setIchAusgefallen(false)
        setAngemeldet(a)
      })
      .catch(() => setIchAusgefallen(true))
      .finally(() => setGefragt(true))
  }, [])

  useEffect(frageWerIchBin, [frageWerIchBin])

  /**
   * **Woran der Bericht hängt** (2.3 Abschnitt 10, R712–R716).
   *
   * Bis zum 2026-09-24 stand hier der Konsolidierungskreis. Er ist damit
   * nicht weg — er ist **eine** der Arten, die ein Organisationselement haben
   * kann, neben Gesellschaft, Ordner und Wert einer eigenen Dimension.
   */
  // Die Auswahl bleibt stehen (R1363) — Element, Rückfrage, Bewertungsebene.
  const [element, setElement] = useGemerkt('kontext.element', '')

  /**
   * Die Rückfrage, wenn ein Element **in mehreren Kreisen** liegt (R715).
   *
   * Der Normalfall: eine Gesellschaft gehört dem Gesamtkonzern und einem
   * Teilkonzern an. Ihre Handelsbilanz II kann je Kreis verschieden ausfallen
   * — andere Bewertungsebene, andere Währung —, und geraten wird nichts.
   */
  const [kreiswunsch, setKreiswunsch] = useGemerkt('kontext.kreis', '')
  const [ledger, setLedger] = useGemerkt('kontext.ledger', '')
  const [periode, setPeriode] = useState(0)

  const [lauf, setLauf] = useState<Rechenantwort | null>(null)
  /*
    Die Gliederung ist eine eigene Achse neben Kreis, Ledger und Periode
    (2.1, Entscheidung 4). Sie steht hier und nicht im Bericht, weil Bilanz
    und GuV dieselbe zeigen müssen — zwei Bildschirme mit verschiedenen
    Gliederungen wären zwei Abschlüsse.
  */
  const [plaene, setPlaene] = useState<Positionsplan[]>([])
  const [plan, setPlan] = useState('')

  /*
    Welche Gliederungen zur eingestellten Bewertung überhaupt gehören. Nicht
    jedes Paar aus Ledger und Plan ist sinnvoll (2.1) — was hier nicht steht,
    lässt sich auch nicht wählen.
  */
  const planwahl = plaene.filter((p) => p.ledger.includes(ledger))

  /*
    **Die Vorräte der Auswahlfelder** (7.2 Abschnitt 10, R583).

    Kreis, Bewertungsebene, Gliederung und Gesellschaft sind Verweisfelder —
    sie suchen ohne Rücksicht auf ihre heutige Länge, weil dieselbe Liste beim
    Kunden zweihundert Zeilen hat. **Die Periode steht bewusst nicht dabei:**
    Sie ist geordnet und bekannt, und für die Eingabe von Hand gibt es das
    Periodenfeld (R502).
  */
  /**
   * Die aufgenommenen Organisationselemente (R712) — nach Art gruppiert, wie
   * sie im Auswahlfeld stehen.
   */
  const elemente = useMemo(() => auswahl?.elemente ?? [], [auswahl])

  const gewaehltesElement = useMemo(
    () => elemente.find((e) => e.schluessel === element) ?? null,
    [elemente, element],
  )

  /**
   * **Der Kreis wird abgeleitet, nicht gewählt** (R716): Zwei Felder für
   * dieselbe Entscheidung gibt es nicht.
   *
   * Genau einer → er gilt. Mehrere → die Rückfrage entscheidet, und solange
   * sie offen ist, steht kein Kreis fest. Keiner → der Bericht zeigt seine
   * Gliederung und keine Zahlen (R715, R717).
   */
  const elementkreise = gewaehltesElement?.kreise ?? []

  const kreis =
    elementkreise.length === 1
      ? (elementkreise[0] ?? '')
      : elementkreise.includes(kreiswunsch)
        ? kreiswunsch
        : ''

  /**
   * Die Gesellschaft, auf die der Bericht **beschränkt** ist (R714) — `null`
   * bei jedem anderen Bezug.
   *
   * Sie schneidet aus dem Lauf des Kreises ihre Spalte heraus: ihre
   * Handelsbilanz II, ohne Konsolidierung und ohne Konzernwert, weil das keine
   * Größen einer einzelnen Gesellschaft sind.
   */
  /**
   * Der Bezug, mit dem die gelieferte Sicht gerufen wird (R751, R752) —
   * `undefined`, wo der gerechnete Lauf gilt.
   *
   * **Ein Kreis gehört nicht dazu:** Seine Zahlen entstehen aus
   * Konsolidierungsschritten, und die gibt es nur im Lauf.
   */
  const gelieferterBezug =
    element && gewaehltesElement?.art !== 'kreis' ? element : undefined

  const bezugsgesellschaft =
    gewaehltesElement?.art === 'gesellschaft' ? gewaehltesElement.objekt : null

  const kreiswahl = useMemo(() => (auswahl?.kreise ?? []).map(ausStammdatum), [auswahl])

  /**
   * Das Auswahlfeld über dem Bericht — **je Art eine Gruppe** (R712).
   *
   * Ohne die Gruppen stünde die Gesellschaft *Holding AG* zwischen zwei
   * Kreisen, und niemand sähe, worauf er gerade berichtet.
   */
  const elementwahl = useMemo(() => {
    /*
      **Die Art steht dabei, sobald es mehr als eine gibt** (R293).

      Sind nur Gesellschaften aufgenommen, sagt „Gesellschaft" hinter jedem
      Eintrag nichts, was der Eintrag daneben nicht auch sagt. Sobald Kreise
      und Gesellschaften nebeneinanderstehen, ist sie die ganze Auskunft:
      Dieselbe *Holding AG* ist als Gesellschaft ihre eigene Bilanz und als
      Kreis der Konzern unter ihr.
    */
    const gemischt = new Set(elemente.map((e) => e.art)).size > 1

    /*
      **Zwei Welten, getrennt** (2.13 Abschnitt 14, R1115): oben die Kreise unter
      „Konzernabschluss", darunter die Gesellschaften unter „Lieferdaten" — mit
      einem Strich dazwischen. Die Art bleibt am Eintrag stehen (R293):
      *Holding AG* gibt es in beiden Gruppen.
    */
    const geordnet = [
      ...elemente.filter((e) => e.art === 'kreis'),
      ...elemente.filter((e) => e.art !== 'kreis'),
    ]

    return geordnet.map((e) => ({
      gruppe: gemischt
        ? t(e.art === 'kreis' ? 'kontext.welt.konzernabschluss' : 'kontext.welt.lieferdaten')
        : undefined,
      wert: e.schluessel,
      text: gemischt
        ? `${beschriftung(e.namen) || e.objekt} — ${t(`stammdaten.wert.art.${e.art}` as Sprachschluessel)}`
        : beschriftung(e.namen) || e.objekt,
      suchtext: [
        e.objekt,
        ...Object.values(e.namen),
        ...Object.values(e.kuerzel ?? {}),
      ].join(' '),
    }))
  }, [elemente])

  /**
   * **Was die Auswahl nicht führt** (R764) — der Satz unter der Liste.
   *
   * **Ein Element wird aufgenommen, nicht angelegt** (R713), und dabei bleibt
   * es: Eine Gesellschaft entsteht dort, wo Gesellschaften entstehen. Der Preis
   * war bis zum 2026-09-25 unsichtbar — wer die Tochter im Bericht suchte,
   * fand sie nicht und erfuhr nicht, woran es liegt.
   *
   * **Er steht nur, wo wirklich etwas fehlt.** Ein Satz, der immer da ist,
   * wird nicht mehr gelesen.
   */
  const aufnahmehinweis = useMemo(() => {
    const rest = auswahl?.nichtAufgenommen
    if (!rest || rest.gesellschaften + rest.kreise === 0) {
      return undefined
    }

    // Einzahl und Mehrzahl stehen als eigene Sätze da, wie überall sonst:
    // „1 Gesellschaften" wäre die Art Satz, die ein Fachanwender einem
    // Programm nachsieht und einem Abschluß nicht.
    const stueck = (anzahl: number, art: 'gesellschaften' | 'kreise') =>
      anzahl === 1
        ? t(`kontext.element.offen.${art}.eins` as Sprachschluessel)
        : t(`kontext.element.offen.${art}.mehrere` as Sprachschluessel, { anzahl: anzahlwort(anzahl) })

    const was = [
      rest.gesellschaften > 0 ? stueck(rest.gesellschaften, 'gesellschaften') : null,
      rest.kreise > 0 ? stueck(rest.kreise, 'kreise') : null,
    ]
      .filter(Boolean)
      .join(t('kontext.element.offen.und'))

    return t('kontext.element.offen', { was })
  }, [auswahl])

  /**
   * Die Rückfrage steht nur, wo es etwas zu fragen gibt (R293, R330) — ein
   * Auswahlfeld mit einem einzigen Eintrag steuert nichts.
   */
  const kreisrueckfrage = useMemo(
    () => kreiswahl.filter((k) => elementkreise.includes(k.wert)),
    [kreiswahl, elementkreise],
  )
  const ledgerwahl = useMemo(() => (auswahl?.ledger ?? []).map(ausStammdatum), [auswahl])
  const gliederungswahl = useMemo(
    () =>
      planwahl.map((p) => ({
        wert: p.schluessel,
        text: beschriftung(p.namen),
        suchtext: p.schluessel,
      })),
    [planwahl],
  )

  const [bilanzstand, setBilanz] = useState<Berichtssatz | null>(null)
  const [guvstand, setGuv] = useState<Berichtssatz | null>(null)
  /*
    **Kam der Bericht nicht, ist er ausgefallen und nicht leer** (7.2
    Abschnitt 39, R1210): Bis zum 2026-10-06 setzte der Fehlerzweig der
    gelieferten Sicht Bilanz und GuV auf „nichts", und da stand „Keine Daten".
    Jetzt hält er den Ausfall fest; solange er gilt, zeigt keine Stelle die
    Zahlen davor, und am Ort des Berichts steht „Ließ sich nicht laden".
  */
  const [berichtausfall, setBerichtausfall] = useState(false)
  /** Zählt *Erneut versuchen* am Bericht — er steht in den Abhängigkeiten des Abrufs. */
  const [berichtsversuch, setBerichtsversuch] = useState(0)
  const bilanz = berichtausfall ? null : bilanzstand
  const guv = berichtausfall ? null : guvstand

  const [vorgaenge, setVorgaenge] = useState<Vorgangskopf[]>([])

  const [fehler, setFehler] = useState<Meldung | null>(null)

  const [pruefung, setPruefung] = useState<Nachweispruefung | null>(null)
  const [messung, setMessung] = useState<Messwerte | null>(null)
  // Neu geholt wird, was in den Tabellen der historischen Kurse fehlt, nach
  // jeder Änderung an ihnen (R1473).
  const [historischVersuch, setHistorischVersuch] = useState(0)
  // Die Datenquellen mit offenen Zielfeldern — gelb im Systemzustand, kein
  // Blocker (R523). Gelesen mit demselben Abruf wie die anderen Zusagen.
  const [datenquellen, setDatenquellen] = useState<Datenquelle[] | null>(null)
  const [urteil, setUrteil] = useState<Freigabeurteil | null>(null)
  const [gibtFrei, setGibtFrei] = useState(false)

  const [ort, setOrt] = useState<Ort>(leseAdresse)
  const [eingeklappt, setEingeklappt] = useState(
    () => window.localStorage.getItem(LEISTE_GEMERKT) === 'eng',
  )

  /*
    Die Sprache liegt im Sprachmodul, nicht hier — sie wird an sehr vielen
    Stellen gebraucht, und sie durch den ganzen Baum zu reichen hieße, jede
    Zwischenstufe von ihr wissen zu lassen. Der Zustand hier ist nur der
    Auslöser fürs Neuzeichnen: Ändert sich die Sprache, zeichnet die Schale
    neu, und jedes `t()` darunter liest den neuen Wert.
  */
  const [sprache, setSprache] = useState(aktuelleSprache)
  const [zahlenformat, setZahlenformat] = useState(aktuellesZahlenformat)
  const [vorzeichen, setVorzeichen] = useState(aktuelleVorzeichenart)
  const [minusfarbe, setMinusfarbe] = useState(aktuelleMinusfarbe)
  // Markierte Wertzellen und ihre Summe, unten rechts (R1016).
  const zellstand = useZellmarkierung()

  // **Vorbelegt ist die Periodenansicht** — für jeden Bericht, im Konzern wie
  // bei einer Gesellschaft (Auftrag vom 2026-10-05). Bis dahin öffnete jeder
  // Bericht im Konsolidierungssachverhalt.
  const [spaltenmodus, setSpaltenmodus] = useState<Spaltenmodus>('perioden')

  /**
   * Welche Spaltenbilder zum Berichtsbezug passen (R714, R759).
   *
   * **Zwei der fünf zeigen den Konzern und sind bei einer Gesellschaft fort:**
   * der Konsolidierungssachverhalt und seine Kurzform führen den Weg zur
   * Konzernzahl vor, den eine einzelne Gesellschaft nicht hat.
   *
   * **Die vier anderen sind Fragen an die Gesellschaft selbst** (R759, seit dem
   * 2026-09-25): *woraus besteht meine Zahl* — in der gelieferten Sicht sind
   * das die **Bewertungsschichten**, Rohdaten und Anpassung —, *woraus nach
   * Gesellschaft* (Konzernstruktur), *gegen wen habe ich Forderungen* (Partner)
   * und *wie hat sich das entwickelt* (Perioden).
   *
   * Bis dahin blieb hier nur eines übrig, und weil eine Wahl mit einem Eintrag
   * keine ist (R293), verschwand die Leiste ganz — der Auftraggeber hat sie
   * vermißt, und zu Recht.
   *
   * **Die Kurzform bleibt draußen:** Sie fasst die Konsolidierungsspalten
   * zusammen, und die gibt es ohne Lauf nicht.
   */
  const bildwahl: Spaltenmodus[] | undefined =
    bezugsgesellschaft === null ? undefined : ['konsolidierung', 'struktur', 'partner', 'perioden']

  useEffect(() => {
    if (bildwahl && !bildwahl.includes(spaltenmodus)) {
      setSpaltenmodus(bildwahl[0]!)
    }
  }, [bildwahl, spaltenmodus])
  const [raster, setRasterRoh] = useState<Raster>('monat')
  const [periodenbericht, setPeriodenbericht] = useState<Periodenbericht | null>(null)

  /*
    Der Zeitraum der Kontextzeile (7.3 Abschnitt 2g): ein Anfang, ein Ende,
    ein Raster. `periode` ist das Ende — der Stichtag, den Bilanz, Spiegel und
    Arbeitsliste kennen; `von` der Anfang, den Periodenbild und
    Kapitalflussrechnung brauchen. Beide sind Monate; das Raster ist die
    Sicht darauf.
  */
  const [von, setVon] = useState(0)
  const [laufperioden, setLaufperioden] = useState<number[]>([])
  /** Ob der mitgegebene Zeitraum schon gewirkt hat — er wirkt genau einmal (R530). */
  const zeitraumGesetzt = useRef(false)
  /**
   * Ob der Zeitraum aus dem Gemerkten stammt und noch niemand ihn gestellt hat
   * — dann sagt der Verweis am Schieber, dass es neuere Zahlen gibt (R1365).
   */
  const [zeitraumGemerkt, setZeitraumGemerkt] = useState(false)

  /**
   * **Womit der Zeitraum anfängt** (R530, R1363, R1364): beim ersten Mal der
   * mitgegebene, sonst der gemerkte; danach der, der steht — auch beim Wechsel
   * der Gesellschaft.
   */
  function startzeitraum(liste: readonly number[]): Zeitraumwahl | null {
    const erstesMal = !zeitraumGesetzt.current
    zeitraumGesetzt.current = true

    const anfang = erstesMal
      ? anfangszeitraum(liste, ZEITRAUM_MITGEGEBEN, ZEITRAUM_BEIM_OEFFNEN)
      : anfangszeitraum(liste, null, alsZeitraum(liesWahl(ZEITRAUM_GEMERKT)))
    if (erstesMal) {
      setZeitraumGemerkt(anfang.gemerkt)
    }
    return anfang.zeitraum
  }

  // Was steht, wird gemerkt — erst wenn der Zeitraum einmal gesetzt ist, sonst
  // überschriebe der Zwischenstand beim Laden das Gemerkte.
  useEffect(() => {
    if (zeitraumGesetzt.current && periode) {
      merkeWahl(ZEITRAUM_GEMERKT, { von: von || periode, bis: periode, raster })
    }
  }, [von, periode, raster])

  /*
    **Auch ohne Kreis hat der Bildschirm eine Zeit** (2.19, R670–R675, und
    2.12 Abschnitt 3.11).

    Die Laufperioden kommen aus den Läufen eines Kreises — steht keiner, kämen
    gar keine, und der Zeitschieber sagte *keine Periode gewählt*. Die
    Zeitachse des Hauses gibt es trotzdem: Sie steht in der Hauseinstellung
    und kommt mit der Auswahl. **Ein Bericht ohne Zahlen braucht sie**, denn
    die Gliederung ist periodenbezogen — welche Position wann gilt, ist eine
    Angabe mit Zeitachse (R1).
  */
  useEffect(() => {
    if (kreis || !auswahl) {
      return
    }

    const liste = [...new Set(auswahl.perioden.map((p) => p.periode))].sort((a, b) => a - b)
    setLaufperioden(liste)

    const anfang = startzeitraum(liste)
    if (anfang) {
      setRasterRoh(anfang.raster)
      setVon(anfang.von)
      setPeriode(anfang.bis)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startzeitraum liest nur Merker
  }, [kreis, auswahl])

  useEffect(() => {
    if (!kreis || !ledger) {
      return
    }

    let gilt = true
    api
      .laufperioden(kreis, ledger)
      .then((liste) => {
        if (!gilt) {
          return
        }

        setLaufperioden(liste)

        /*
          **Der mitgegebene Zeitraum gilt einmal** (R530) — danach ist dieses
          Fenster für sich. Wer den Kreis wechselt, behält den Zeitraum, der
          steht, solange es dort Zahlen dafür gibt (R1363).
        */
        const anfang = startzeitraum(liste)
        if (anfang) {
          setRasterRoh(anfang.raster)
          setVon(anfang.von)
          setPeriode(anfang.bis)
        }
      })
      .catch((f: Error) => gilt && setFehler(alsMeldung(f)))

    return () => {
      gilt = false
    }
  }, [kreis, ledger])

  /**
   * Ein neuer Zeitraum vom Schieber: Der Anfang ist der erste Monat der
   * linken Zeiteinheit, das Ende der **Stichtag** der rechten — ihr letzter Monat
   * mit Lauf. Wer Q2 wählt, sieht in der Bilanz den Juni.
   */
  function waehleZeitraum(anfang: number, ende: number) {
    setZeitraumGemerkt(false)
    setVon(anfang)
    setPeriode(stichtag(einheitVon(ende, raster), laufperioden))
  }

  /** Der jüngste Monat mit Zahlen, wenn er nach dem gemerkten Ende liegt (R1365). */
  const juengsteperiode = zeitraumGemerkt ? neuereZahlen(laufperioden, periode) : null

  /** Der Verweis: das Geschäftsjahr bis zum jüngsten Monat, im stehenden Raster. */
  function zeigeJuengste() {
    const vorgabe = vorbelegung(laufperioden)
    if (vorgabe) {
      waehleZeitraum(einheitVon(vorgabe.von, raster).von, vorgabe.bis)
    }
  }

  /**
   * Das Raster wechseln: Beide Enden werden **nach außen gerundet** — April
   * wird Q2, Februar bis April wird Q1 bis Q2. Der Bericht zeigt den Wert
   * der Zeiteinheit, in der der gewählte Monat liegt, und der Schieber liest
   * sich in der neuen Zeiteinheit.
   */
  function setRaster(neu: Raster) {
    setZeitraumGemerkt(false)
    setRasterRoh(neu)
    const gerundet = runde(von || periode, periode, neu)
    setVon(gerundet.von)
    setPeriode(stichtag({ von: einheitVon(periode, neu).von, bis: gerundet.bis }, laufperioden))
  }
  const [spiegel, setSpiegel] = useState<Spiegelsatz | null>(null)

  /** Welcher Spiegel hinter welchem Menüpunkt steht. */
  const spiegelart: Record<string, string> = {
    spiegel: 'anlagen',
    eigenkapitalspiegel: 'eigenkapital',
    kreditspiegel: 'verbindlichkeiten',
  }
  /*
    Wie die Zeitreihe gelesen wird — einzeln oder kumuliert (7.3 Abschnitt 2a,
    R426). **Zwei Wahlen und nicht eine**: Die Bilanz öffnet auf *Saldo*
    (kumuliert), GuV und Kapitalflussrechnung auf *Monatswert* (einzeln). Eine
    gemeinsame Wahl müsste einen der beiden Bildschirme falsch aufmachen.

    Sie liegen hier und nicht in den Bauteilen, weil sie den Wechsel zwischen
    den Berichten überdauern sollen (R173): Wer aus der GuV kurz in die Bilanz
    sieht, findet sein YTD bei der Rückkehr wieder.
  */
  const [bilanzwert, setBilanzwert] = useState<Zeitwert>(ZEITWERTVORGABE.bestand)
  const [stromwert, setStromwert] = useState<Zeitwert>(ZEITWERTVORGABE.strom)

  /** Welche der beiden Wahlen dieser Bildschirm bedient. */
  const zeitwertfamilie = ort.unterpunkt === 'bilanz' ? ('bestand' as const) : ('strom' as const)
  const zeitwert = zeitwertfamilie === 'bestand' ? bilanzwert : stromwert

  /** Die Verkehrszahl der Bilanz: die Veränderung zur Vorspalte statt des Bestands. */
  const verkehrszahl = bilanzwert === 'einzeln'
  /** Year to date in GuV und Kapitalflussrechnung (R427) — gerechnet wird im Dienst. */
  const kumuliert = stromwert === 'kumuliert'

  /*
    Die Kapitalflussrechnung (2.8, G3).

    Sie liegt hier und nicht im Bauteil, weil sie denselben Kontext liest wie
    Bilanz und GuV — Kreis, Ledger, Periode aus der Kontextzeile — und
    dieselbe Spaltenwahl bedient: Das Raster entscheidet, über welchen
    Zeitraum gerechnet wird, denn eine Kapitalflussrechnung ist eine
    Stromgröße und braucht einen Zeitraum, keinen Stichtag.

    Die Stichtagskursanpassung ist ein eigener Zustand und kein Zeitwert: Der
    Zeitwert liest dieselbe Reihe anders, hier ändern sich die Zeilenwerte.
    Beide Stellungen sind richtig — die Rechnung geht in beiden auf.
  */
  const [kapitalfluss, setKapitalfluss] = useState<Kfrsatz | null>(null)
  const [kfrPerioden, setKfrPerioden] = useState<Kfrperiodenbericht | null>(null)
  const [kursanpassung, setKursanpassung] = useState(true)

  /*
    Der Zähler, der die Rechnung neu holt.

    Eine erfasste Umgliederung wirkt erst mit dem nächsten Lauf auf die
    Zahlen — aber die Liste darunter zeigt sie sofort, und die Rechnung
    darf nicht stehen bleiben, während daneben schon etwas anderes steht.
    Ein Zähler und kein Zustandsobjekt: Er sagt nur „hol neu", und was neu
    zu holen ist, weiß der Abrufer selbst.
  */
  const [kfrStand, setKfrStand] = useState(0)

  /*
    Der Aufklappzustand liegt hier und nicht in den drei Bildschirmen: Der
    Aufriss ist ein Pfad auf der Zeilenachse und gilt in allen Spaltenbildern
    (R171) — wer in der Bilanz eine Position geöffnet hat und auf Perioden
    umschaltet, findet sie offen wieder. Und die Suche braucht ihn, um einen
    Treffer aufzuklappen, der noch zugeklappt ist.
  */
  const aufriss = useAufriss()

  /*
    Die Zusatzachsen des Laufs — Partner, Segment, die Analysedimensionen des
    Kunden — und ob sie belegt sind (7.3 Abschnitt 2f). Einmal je Lauf
    geholt; jeder Bericht bietet sie im Menü an.
  */
  // Im Hintergrund (R1007, R1211): ohne Achsen bleibt der Aufriss bei der
  // Vorgabe — ein Menüangebot, keine Aussage über den Bestand.
  const achsen = useHintergrundabruf(lauf ? () => api.achsen([lauf.laufId]) : null, [lauf], KEINE_ACHSEN)

  // Gemerkt wird, was kam — der Ersatz nicht, wie bisher.
  useEffect(() => {
    if (achsen !== KEINE_ACHSEN) {
      merkeAchsen(achsen)
    }
  }, [achsen])

  /*
    Das Partnerbild: die gewählte Gesellschaft — leer heißt der ganze Kreis —
    und der Würfel je Partner, auf Abruf geholt, sobald das Bild steht.
  */
  // Ein anderer Kreis hat andere Gesellschaften: Gemerkt wird je Kreis (R1363).
  const [gesellschaft, setGesellschaft] = useGemerkt(`partnerbild.gesellschaft.${kreis}`, '')
  const [partnerwuerfel, setPartnerwuerfel] = useState<Zusatzzelle[]>([])
  const [kfrPartnerwuerfel, setKfrPartnerwuerfel] = useState<Kfrzusatzzelle[]>([])

  const berichtsart: 'bilanz' | 'guv' = ort.unterpunkt === 'guv' ? 'guv' : 'bilanz'

  /*
    **Ob die Handelsbilanz II ihre Schichten zeigt, steht in der Adresse**
    (R402, R156) — nicht in einer gemerkten Vorliebe. Wer einen Verweis
    weitergibt, gibt die Tabelle weiter, die er vor sich hat; eine Vorliebe
    läge im Browser des einen und der andere sähe etwas anderes.
  */
  const schichtenOffen = ort.ziel.schichten === 'auf'

  /*
    **Welche Teilkonzerne aufgeklappt sind** (R407) — ebenfalls in der Adresse.
    Kommagetrennt, weil es mehrere sein können: `?tk=EH-FR,EH-USA`.
  */
  const offeneStufen = new Set(
    (ort.ziel.tk ?? '').split(',').filter((s) => s.length > 0),
  )

  /*
    **Welche Dimensionen ihre Nullzeilen unterdruecken, steht in der Adresse**
    (7.3 Abschnitt 11, R419-R421) -- wie der Aufriss und die Schichten und aus
    demselben Grund (R156): Ein weitergegebener Verweis zeigt die Tabelle, die
    der Absender vor sich hat. Eine ausgeblendete Zeile ist ein Unterschied im
    Inhalt, keine Vorliebe des Bildschirms; die Spaltenbreite ist das Gegenbild
    dazu und liegt deshalb im Browser (R417).

    Kommagetrennt, weil es mehrere sein koennen: `?ohne=positionen,einheiten`.
  */
  const anzeigeoptionen = {
    ohneNull: new Set((ort.ziel.ohne ?? '').split(',').filter((d) => d.length > 0)),
    // **Die Zwischensummen ebenso** (7.3 Abschnitt 26, R1018): Wer einen
    // Verweis weitergibt, gibt die Spalten weiter, die er sieht.
    // `?zwischen=quartal,jahr`.
    zwischensummen: new Set((ort.ziel.zwischen ?? '').split(',').filter((d) => d.length > 0)),
  }

  function zwischensummeUmschalten(art: string, an: boolean) {
    const naechste = new Set(anzeigeoptionen.zwischensummen)
    if (an) {
      naechste.add(art)
    } else {
      naechste.delete(art)
    }

    setzeZiel('zwischen', naechste.size === 0 ? null : [...naechste].join(','))
  }

  function nullzeilenUmschalten(dimension: string, an: boolean) {
    const naechste = new Set(anzeigeoptionen.ohneNull)
    if (an) {
      naechste.add(dimension)
    } else {
      naechste.delete(dimension)
    }

    setzeZiel('ohne', naechste.size === 0 ? null : [...naechste].join(','))
  }

  /*
    **Welche Gliederungspositionen zugeklappt sind** (7.3 Abschnitt 13, R446) —
    kommagetrennt in der Adresse, wie die Teilkonzerne: `?zu=pos-A,pos-B.II`.

    Aus demselben Grund wie dort (R156): Eine zusammengeschobene Gliederung ist
    ein Unterschied im **Inhalt**. Wer einen Verweis weitergibt, gibt die
    Tabelle weiter, die er vor sich hat — nicht eine, die beim Empfänger anders
    aussieht.
  */
  const zugeklappt = new Set(
    (ort.ziel.zu ?? '').split(',').filter((p) => p.length > 0),
  )

  function gliederungUmschalten(position: string) {
    const naechste = new Set(zugeklappt)
    if (!naechste.delete(position)) {
      naechste.add(position)
    }

    gliederungSetzen(naechste)
  }

  function gliederungSetzen(positionen: ReadonlySet<string>) {
    setzeZiel('zu', positionen.size === 0 ? null : [...positionen].join(','))
  }

  function stufeUmschalten(schluessel: string) {
    const naechste = new Set(offeneStufen)
    if (!naechste.delete(schluessel)) {
      naechste.add(schluessel)
    }

    setzeZiel('tk', naechste.size === 0 ? null : [...naechste].join(','))
  }

  /** Setzt oder entfernt einen Wert in der Adresse, ohne den Ort zu wechseln. */
  function setzeZiel(feld: string, wert: string | null) {
    const frage = new URLSearchParams(ort.ziel)

    if (wert === null) {
      frage.delete(feld)
    } else {
      frage.set(feld, wert)
    }

    const pfad = ort.unterpunkt ? `${ort.bereich}/${ort.unterpunkt}` : ort.bereich
    const angehaengt = frage.toString()
    window.location.hash = angehaengt ? `${pfad}?${angehaengt}` : pfad
  }

  function schichtenUmschalten() {
    setzeZiel('schichten', schichtenOffen ? null : 'auf')
  }

  useEffect(() => {
    if (spaltenmodus !== 'partner' || ort.bereich !== 'berichte' || (ort.unterpunkt !== 'bilanz' && ort.unterpunkt !== 'guv')) {
      return
    }

    /*
      **Dieselbe Frage, zwei Quellen** (R759, R751): Der gerechnete Lauf liest
      seine festgeschriebenen Zeilen, die gelieferte Sicht das Journal. Was
      danach kommt — Verdichtung, Spalten, Aufriss — ist beides Mal dasselbe.

      *Gegen wen habe ich Forderungen* ist eine Frage an die Gesellschaft, die
      liefert; sie auf einen Abschlußlauf zu vertagen hieße, sie erst zu
      beantworten, wenn ein Konzern gerechnet ist.
    */
    const holen = gelieferterBezug
      ? api.gelieferterAufriss({
          bezug: gelieferterBezug,
          ledger,
          periode,
          art: berichtsart,
          plan: plan || null,
          achse: 'partner',
          einheit: gesellschaft || null,
        })
      : lauf
        ? api.aufriss({ lauf: lauf.laufId, art: berichtsart, plan: plan || null, achse: 'partner', einheit: gesellschaft || null })
        : null

    if (!holen) {
      return
    }

    let gilt = true
    setPartnerwuerfel([])
    holen
      .then((a) => gilt && setPartnerwuerfel(a.zellen))
      .catch((f: Error) => gilt && setFehler(alsMeldung(f)))

    return () => {
      gilt = false
    }
  }, [spaltenmodus, lauf, gelieferterBezug, ledger, periode, plan, berichtsart, gesellschaft, ort.bereich, ort.unterpunkt])

  useEffect(() => {
    if (spaltenmodus !== 'partner' || ort.unterpunkt !== 'kapitalflussrechnung' || !kreis || !ledger || !periode) {
      return
    }

    let gilt = true
    setKfrPartnerwuerfel([])
    api
      .kapitalflussAufriss({
        kreis,
        ledger,
        bis: periode,
        raster,
        waehrungsdelta: kursanpassung,
        von: von || undefined,
        achse: 'partner',
        einheit: gesellschaft || null,
        // Die gewählte Gesellschaft liest ihre gelieferten Daten, nicht den Lauf (R759).
        bezug: gelieferterBezug,
      })
      .then((a) => gilt && setKfrPartnerwuerfel(a.zellen))
      .catch((f: Error) => gilt && setFehler(alsMeldung(f)))

    return () => {
      gilt = false
    }
  }, [spaltenmodus, ort.unterpunkt, kreis, ledger, periode, von, raster, kursanpassung, gesellschaft, kfrStand, gelieferterBezug])

  /*
    Woher jeder Bericht die Zellen einer Zusatzachse holt — je Bericht eine
    Quelle, alle mit demselben Angebot. Die Kennung trägt den Lauf: Ein
    anderer Lauf ist ein anderer Vorrat, und der Zwischenspeicher darf ihn
    nicht verwechseln.
  */
  const letzteZelle = (pfad: Zelle[]) => pfad[pfad.length - 1] ?? { einheit: null, konto: null }
  const mitWeiteren = <T extends { wert: string | null }>(zellen: T[], pfad: Zelle[], achse: string) =>
    zellen.map((z) => ({ ...z, weitere: { ...weitereVon(pfad), [achse]: z.wert } }))

  const berichtsquelle = useMemo<Achsenquelle<Aufrisszelle> | undefined>(
    () =>
      lauf
        ? {
            kennung: `bericht:${lauf.laufId}:${berichtsart}:${plan}`,
            angebot: achsen,
            lade: async (position, pfad, achse) => {
              const l = letzteZelle(pfad)
              const a = await api.aufriss({
                lauf: lauf.laufId,
                art: berichtsart,
                plan: plan || null,
                position,
                achse,
                einheit: l.einheit,
                konto: l.konto,
                weitere: weitereVon(pfad),
              })
              /*
                Die Karte wird zur Reihe (R402): Der Bericht führt die
                Schichten in EINER Folge, und die nachgeladene Zelle kennt sie
                nicht. Fehlt eine, steht dort eine Null — und das ist hier die
                richtige Auskunft, denn die Zelle hat zu dieser Schicht
                tatsächlich nichts beigetragen.
              */
              const folge = (berichtsart === 'guv' ? guv : bilanz)?.schichten ?? []

              return mitWeiteren(a.zellen, pfad, achse).map((z) => ({
                ...z,
                schichten: folge.map((s) => z.schichten?.[s.schluessel] ?? 0),
              }))
            },
          }
        : undefined,
    [lauf, berichtsart, plan, achsen, bilanz, guv],
  )

  const periodenquelle = useMemo<Achsenquelle<Periodenaufriss> | undefined>(
    () =>
      kreis && ledger && periode
        ? {
            kennung: `perioden:${kreis}:${ledger}:${berichtsart}:${raster}:${von}:${periode}:${kumuliert}`,
            angebot: achsen,
            lade: async (position, pfad, achse) => {
              const l = letzteZelle(pfad)
              const a = await api.periodenAufriss({
                kreis,
                ledger,
                art: berichtsart,
                raster,
                bis: periode,
                von: von || undefined,
                // Eine umgehängte Position heißt hier `Position~Stelle` und
                // klappt nur ihre Monate an dieser Stelle auf (R1723).
                position: positionAus(position),
                stelle: stelleAus(position),
                achse,
                einheit: l.einheit,
                konto: l.konto,
                weitere: weitereVon(pfad),
                kumuliert: berichtsart === 'guv' && kumuliert,
              })
              return mitWeiteren(a.zellen, pfad, achse)
            },
          }
        : undefined,
    [kreis, ledger, periode, von, berichtsart, raster, achsen, kumuliert],
  )

  const spiegelquelle = useMemo<Achsenquelle<Spiegelaufriss> | undefined>(() => {
    const art = spiegelart[ort.unterpunkt ?? '']
    if (!art || !kreis || !ledger || !periode) {
      return undefined
    }

    return {
      kennung: `spiegel:${art}:${kreis}:${ledger}:${periode}`,
      angebot: achsen,
      lade: async (position, pfad, achse) => {
        const l = letzteZelle(pfad)
        const a = await api.spiegelAufriss({
          kreis,
          ledger,
          bis: periode,
          art,
          position,
          achse,
          einheit: l.einheit,
          konto: l.konto,
          weitere: weitereVon(pfad),
        })
        return mitWeiteren(a.zellen, pfad, achse)
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ort.unterpunkt, kreis, ledger, periode, achsen])

  const kfrquelle = useMemo<Achsenquelle<Kfrbeitrag> | undefined>(
    () =>
      kreis && ledger && periode
        ? {
            kennung: `kfr:${kreis}:${gelieferterBezug}:${ledger}:${von}:${periode}:${raster}:${kursanpassung}:${kfrStand}`,
            angebot: achsen,
            lade: async (zeile, pfad, achse) => {
              const l = letzteZelle(pfad)
              const a = await api.kapitalflussAufriss({
                kreis,
                ledger,
                bis: periode,
                von: von || undefined,
                raster,
                waehrungsdelta: kursanpassung,
                zeile,
                achse,
                einheit: l.einheit,
                konto: l.konto,
                weitere: weitereVon(pfad),
                bezug: gelieferterBezug,
              })
              return mitWeiteren(a.zellen, pfad, achse)
            },
          }
        : undefined,
    [kreis, gelieferterBezug, ledger, periode, von, raster, kursanpassung, kfrStand, achsen],
  )

  const kfrPeriodenquelle = useMemo<Achsenquelle<Kfrperiodenaufriss> | undefined>(
    () =>
      kreis && ledger && periode
        ? {
            kennung: `kfrperioden:${kreis}:${gelieferterBezug}:${ledger}:${von}:${periode}:${raster}:${kursanpassung}:${kfrStand}:${kumuliert}`,
            angebot: achsen,
            lade: async (zeile, pfad, achse) => {
              const l = letzteZelle(pfad)
              const a = await api.kapitalflussPeriodenAufriss({
                kreis,
                ledger,
                raster,
                bis: periode,
                von: von || undefined,
                waehrungsdelta: kursanpassung,
                zeile,
                achse,
                einheit: l.einheit,
                konto: l.konto,
                weitere: weitereVon(pfad),
                kumuliert,
                bezug: gelieferterBezug,
              })
              return mitWeiteren(a.zellen, pfad, achse)
            },
          }
        : undefined,
    [kreis, gelieferterBezug, ledger, periode, von, raster, kursanpassung, kfrStand, achsen, kumuliert],
  )

  /*
    Der Wertenachweis liegt hier und nicht im Bericht: Er wird aus drei
    Spaltenbildern heraus geöffnet und braucht Kreis, Ledger und Periode aus
    der Kontextzeile. Ein Fenster über dem Bericht, kein Ortswechsel (R204) —
    deshalb ein Zustand und keine Adresse.
  */
  const [nachweis, setNachweis] = useState<Nachweisziel | null>(null)

  /**
   * Die Buchungsmaske über dem Bericht (2.16, R385).
   *
   * Wie der Wertenachweis ein **Fenster über dem Bericht und kein
   * Ortswechsel**: Der Bericht bleibt stehen, das Fenster geht wieder zu, und
   * der Anwender ist da, wo er war.
   */
  const [buchung, setBuchung] = useState<Buchungsvorbelegung | null>(null)

  /**
   * Was der Rechtsklick anlegen soll — die fünfte Menügruppe (R494).
   *
   * Wie die Buchungsmaske ein **Fenster über dem Bericht und kein
   * Ortswechsel**: Der Bericht bleibt stehen, das Fenster geht wieder zu, und
   * der Anwender ist da, wo er war.
   */
  const [anlage, setAnlage] = useState<Anlageziel | null>(null)

  /** Welche Sicht das Journal gerade zeigt — Ordner oder chronologisch. */
  const [journalsicht, setJournalsicht] = useState<'ordner' | 'suche' | 'chronologisch'>(
    'ordner',
  )

  /**
   * Die Gesellschaft des Journals — **eine für beide Sichten.**
   *
   * Zwei eigene Auswahlfelder, die auseinanderlaufen können, wären genau die
   * Sorte Oberfläche, bei der man nach dem Wechsel der Sicht erst einmal
   * nachsieht, wo man gelandet ist.
   */
  const [journaleinheit, setJournaleinheit] = useGemerkt('journal.gesellschaft', '')

  /**
   * **Die Periode des Journals — seine eigene, und sie steht auf „Alle"**
   * (2.16 Abschnitt 5.6, R986). `0` heißt: alle Perioden.
   *
   * Bis zum 2026-10-03 war das Feld über dem Journal der Stichtag der
   * Berichte: Wer im Journal Oktober wählte, stellte die Konzernbilanz mit um.
   * Der Auftraggeber: *„Vorauswahl ‚Alle Perioden' bitte setzen."*
   */
  const [journalperiode, setJournalperiode] = useState(0)

  /** Woher der Sprung aus der Trefferliste kommt — Ordner auf, Beleg aufgeschlagen (R209). */
  const [journalziel, setJournalziel] = useState<{
    einheit: string
    ordnerId: number | null
    belegId: number
  } | null>(null)

  /**
   * **Stabil, und das ist hier keine Feinheit.**
   *
   * Als Inline-Funktion bekam dieser Griff bei jedem Zeichnen eine neue
   * Identität. Der Effekt im Belegordner, der den Sprung ausführt, lief
   * dadurch neu, sein Aufräumen verwarf den Lauf, und die noch laufende
   * Abfrage nach dem Beleg wurde verworfen: Der Ordner ging auf, der Beleg
   * blieb zu. Aufgefallen am 2026-09-12 beim ersten Sprung aus der
   * Trefferliste.
   */
  const journalzielErreicht = useCallback(() => setJournalziel(null), [])

  /**
   * Der Rückweg zu den Datenimporten (5.1 Abschnitt 3.61, R1246) — der Stand
   * der Liste, aus der zum Beleg gesprungen wurde; `null`, wenn niemand von
   * dort kam.
   */
  const [journalrueckweg, setJournalrueckweg] = useState<string | null>(null)

  /*
    **Der Sprung aus den Datenimporten** (5.1 Abschnitt 3.61, R1245): Die
    Adresse nennt Beleg, Gesellschaft und Ordner. Gewählt werden Ordnersicht,
    Gesellschaft und alle Perioden, aufgeschlagen wird mit dem Sprung aus R209.
    Danach vergisst die Adresse die Angaben — sonst schlüge jedes Neuladen den
    Beleg wieder auf. Wer das Journal verlässt, verliert den Rückweg.
  */
  useEffect(() => {
    if (ort.bereich !== 'nachweis' || ort.unterpunkt !== 'buchungsjournal') {
      setJournalrueckweg(null)
      return
    }

    const ziel = belegzielLesen(ort.ziel)
    if (!ziel) {
      return
    }

    setJournalsicht('ordner')
    setJournaleinheit(ziel.einheit)
    setJournalperiode(0)
    setJournalziel(ziel)
    setJournalrueckweg(ort.ziel.zurueck ?? null)
    window.location.hash = '#/nachweis/buchungsjournal'
  }, [ort])

  /** Die Gesellschaften, die das Journal zur Wahl stellt. */
  const [journaleinheiten, setJournaleinheiten] = useState<
    { schluessel: string; code: string; namen: Record<string, string> }[]
  >([])

  // Dieselbe Liste wie oben in der Kontextzeile, nur mit dem Mittelpunkt, den
  // die Journalleiste seit je trägt.
  //
  // **Der vergebene Schlüssel steht nicht mehr davor** (R699, 2026-09-22):
  // Hier stand „EH-000004-60 · Nordlicht Toulouse S.A.S.", während überall
  // sonst nur der Name steht. Der Grund war, dass diese Auswahl ihren Text
  // selbst zusammensetzte, statt ihn zu erfragen.
  const journalwahl = useMemo(
    () =>
      journaleinheiten.map((e) => ({
        wert: e.schluessel,
        text: mitCode(e.code, beschriftung(e.namen), ' · ', e.schluessel),
        suchtext: [e.schluessel, ...Object.values(e.namen)].join(' '),
      })),
    [journaleinheiten],
  )

  /**
   * Die Perioden, die das Journal zur Wahl stellt.
   *
   * **Was geliefert wurde, nicht was gerechnet wurde.** Ein Beleg liegt im
   * Journal, ob jemand gerechnet hat oder nicht — die Laufperioden wären hier
   * die falsche Liste und ließen Monate weg, in denen Belege stehen.
   */
  const journalperioden = useMemo(
    () =>
      [...new Set((auswahl?.perioden ?? []).map((p) => p.periode))].sort((a, b) => b - a),
    [auswahl],
  )

  // Was der Gliederungsbaum angeklickt hat — es schlägt im Blatt darunter auf
  // (R317). Der Zähler daneben lässt den Baum neu lesen, wenn das Blatt
  // geschrieben hat: Beide zeigen dieselben Zeilen.
  const [baumwahl, setBaumwahl] = useState<{ objektart: string; schluessel: string } | null>(null)
  const [baumstand, setBaumstand] = useState(0)

  /*
    **Was angelegt ist, steht im Bericht** (R569, 7.4 Abschnitt 14.4,
    berichtigt am 2026-09-16).

    Derselbe Zähler für Bilanz, GuV, Spiegel und Kapitalflussrechnung: Er
    steht in den Abhängigkeiten ihrer drei Ladevorgänge, und jeder von ihnen
    geht über die **Laufvorsorge** (R294). Die holt den Lauf nach, sobald der
    Datenstand jünger ist als er — das Anlegen löst also keinen Lauf aus, es
    stellt nur die Frage neu, auf die die Vorsorge ohnehin antwortet.
  */
  const [anlagestand, setAnlagestand] = useState(0)

  /*
    **Die Anzeigewährung** (2.12 Abschnitt 3.21, R993, R994): gewählt über dem
    Bericht, gültig für alle Berichte, bis sie geändert wird — und nach dem
    Neuladen wieder die Ausgangswährung, weil sie nirgends gespeichert wird.

    Mitgeschickt wird sie von der Schnittstellenschicht an jedem Berichtsabruf.
    Gesetzt wird sie dort schon beim Zeichnen, damit jeder Abruf dieses Zugs sie
    kennt; ändert sie sich, stellt `anlagestand` die Frage an alle Berichte neu.
  */
  const [anzeigewaehrung, setAnzeigewaehrung] = useState<string | null>(null)
  // Was zur Wahl steht, hängt am Bezug: der Konzern oder die Gesellschaft (R994).
  // Im Hintergrund (R1007, R1211): Ohne Angebot steht der Bericht in seiner
  // Ausgangswährung — das ist keine Störung, die ein Fenster verdiente.
  const waehrungsangebot = useHintergrundabruf<Anzeigewaehrungen | null>(
    ort.bereich !== 'berichte' || !periode || !(gelieferterBezug || kreis)
      ? null
      : () => api.anzeigewaehrungen({ kreis, bezug: gelieferterBezug, periode }),
    [ort.bereich, kreis, gelieferterBezug, periode],
    null,
  )
  const wirksameWaehrung = wirksameAnzeigewaehrung(anzeigewaehrung, waehrungsangebot)
  setzeAnzeigewaehrung(wirksameWaehrung)

  /*
    **Womit der Bericht umgerechnet ist, der gerade zu sehen ist** (R996) — und
    ob überhaupt. Bleibt er in seiner Ausgangswährung, bleiben es auch die
    Nachweise und Aufrisse, die aus ihm geöffnet werden (2.12 Abschnitt 3.26,
    R1117). Bis zum 2026-10-05 stand die Kapitalflussrechnung der Tochter USA in
    USD und der Nachweis ihrer Zelle in EUR.
  */
  const berichtsumrechnung =
    ort.bereich !== 'berichte'
      ? null
      : ort.unterpunkt === 'bilanz' || ort.unterpunkt === 'guv'
        ? spaltenmodus === 'perioden'
          ? periodenbericht?.umrechnung
          : (ort.unterpunkt === 'bilanz' ? bilanz : guv)?.umrechnung
        : ort.unterpunkt === 'kapitalflussrechnung'
          ? spaltenmodus === 'perioden' && kreis
            ? kfrPerioden?.umrechnung
            : kapitalfluss?.umrechnung
          : ['spiegel', 'eigenkapitalspiegel', 'kreditspiegel'].includes(ort.unterpunkt ?? '')
            ? spiegel?.umrechnung
            : null
  setzeBerichtsumrechnung(berichtsumrechnung ?? null)
  const vorigeWaehrung = useRef<string | null>(null)

  useEffect(() => {
    if (vorigeWaehrung.current !== wirksameWaehrung) {
      vorigeWaehrung.current = wirksameWaehrung
      setAnlagestand((n) => n + 1)
    }
  }, [wirksameWaehrung])

  /** Das Stammdatum, dessen Eigenschaften gerade im Fenster stehen (R858). */
  const [eigenschaften, setEigenschaften] = useState<Eigenschaftsziel | null>(null)
  // Rechtsklick → Reihenfolge ändern in Bilanz und GuV (7.4 Abschnitt 32, R1341).
  const [reihenfolge, setReihenfolge] = useState<Reihenfolgeziel | null>(null)
  /*
    **Die Gliederung wird geholt, wo kein Lauf steht** (R717).

    Nicht daneben und nicht vorsorglich: Solange ein Lauf da ist, zeigt der
    Bericht seine Zahlen, und ein zweiter Abruf beantwortete dieselbe Frage
    ein zweites Mal. Erst wenn nichts gerechnet ist, ist die Struktur die
    Auskunft.

    **`anlagestand` steht dabei**, wie bei den gerechneten Berichten: Wer im
    leeren Bericht eine Position anlegt (R719), soll sie sofort sehen.
  */
  /*
    Die Gliederung aus den Stammdaten — für den Fall, dass **nichts gerechnet
    ist** (2.12 Abschnitt 3.11, R717). Sie kommt aus demselben Dienst, der sie
    im Zuordnungstisch zeigt (R307): Positionen mit ihren Konten, zum heutigen
    Stand und zur gewählten Periode. Daraus wird ein Berichtssatz ohne Zahlen.

    **Ein Hauptabruf** (7.2 Abschnitt 39, R1211): Ohne sie steht „Keine
    Daten" da — und das nur, wenn feststeht, dass es keine gibt.
  */
  const gliederungsabruf = useAbruf<Gliederungsbild | null>(
    lauf || !ledger || !periode || ort.bereich !== 'berichte'
      ? null
      : () =>
          // **Ohne freigegebenen Plan antwortet der Dienst leer** (`null`) — es
          // bleibt bei der Meldung, dass nichts vorliegt. Antwortet er nicht oder
          // bricht er ab, ist das ein Ausfall und kein „nichts" (R1210).
          api.gliederung(ledger, periode, plan || undefined),
    [lauf, ledger, periode, plan, ort.bereich, anlagestand],
  )
  const gliederungsbild = gliederungsabruf.wert


  /*
    Der Nachweis aus der Kapitalflussrechnung ist ein eigener Zustand und
    nicht derselbe: Er erklärt eine andere Sorte Zahl und hat deshalb eine
    andere Form (2.8 Abschnitt 13.14). Was er mit dem Bilanznachweis teilt,
    ist der Weg hinaus — beide führen an dieselbe Belegzeile im Journal
    (R156, R209).
  */
  const [kfrNachweis, setKfrNachweis] = useState<Kfrnachweisziel | null>(null)

  const [sucheOffen, setSucheOffen] = useState(false)
  const [suchtext, setSuchtext] = useState('')
  const [suchstelle, setSuchstelle] = useState(0)

  /*
    Durchsucht wird der ganze Bericht, auch das noch Zugeklappte — die Zahlen
    sind ohnehin geladen. Welche es sind, hängt am Spaltenbild: Im
    Periodenbild auf Verkehrszahl oder YTD stehen andere Zahlen auf dem Schirm
    als die Bestände, und gesucht wird, was dort steht.
  */
  const berichtssatz = ort.unterpunkt === 'guv' ? guv : bilanz
  const suchgut = useMemo(() => {
    if (spaltenmodus === 'perioden') {
      // Ohne gerechnete Periode zeigt das Periodenbild keine Tabelle, sondern
      // einen Hinweis. Dann gibt es auch nichts zu suchen: Ein Zähler, der
      // Treffer meldet, die nirgends stehen, ist schlimmer als kein Zähler.
      // Eine einzelne Spalte ist eine Tabelle (R528) und wird durchsucht.
      return periodenbericht && periodenbericht.spalten.length > 0
        ? suchgutPerioden(periodenbericht, (zeile, zelle) =>
            reihe(
              zelle ? zelle.werte : zeile.werte,
              periodenbericht.spalten,
              verkehrszahl,
              zelle ?? { einheit: null, konto: null },
              zeile.beitraege,
            ),
          )
        : []
    }

    // Im Struktur-, Kurz- und Partnerbild stehen Summe, Konsolidierung und
    // Konzern; die Gesellschaftsspalten nur im Strukturbild. Die
    // Partnerspalten werden nicht durchsucht — sie sind nachgeladen und
    // nicht Teil des Berichts.
    return berichtssatz
      ? suchgutBericht(
          berichtssatz,
          spaltenmodus === 'konsolidierung' ? 'konsolidierung' : 'struktur',
          spaltenmodus === 'struktur' ? einheitenIm(berichtssatz) : [],
        )
      : []
  }, [spaltenmodus, periodenbericht, berichtssatz, verkehrszahl, sprache])

  const funde = useMemo(() => suchen(suchgut, suchtext), [suchgut, suchtext])

  // Was die Tabelle davon braucht: welche Zeilen Treffer sind, in welchen
  // Spalten der Betrag steht und welcher Treffer gerade dran ist.
  const treffer = useMemo(
    () => ({
      zellen: new Map(funde.map((f) => [f.id, new Set(f.spalten)])),
      aktiv: funde[suchstelle]?.id ?? null,
    }),
    [funde, suchstelle],
  )

  /**
   * Zum Treffer springen: erst den Pfad öffnen, dann hinrollen.
   *
   * Das Aufklappen ändert den Zustand, gezeichnet wird danach — deshalb wartet
   * das Rollen einen Rahmen ab. Ohne das führte der Sprung an die Stelle, an
   * der die Zeile vor dem Aufklappen stand.
   */
  const springeZu = useCallback(
    (stelle: number) => {
      const fund = funde[stelle]
      if (!fund) {
        return
      }

      setSuchstelle(stelle)
      aufriss.pfadOeffnen(fund.position, fund.pfad)

      /*
        **Und der Treffer holt seine Zeile hervor** (R177, R446): Liegt er
        unter einer zugeklappten Gliederungsposition, geht sie auf. Sonst
        meldete der Zähler einen Fund, den niemand sieht — gesucht wird der
        ganze Bericht, nicht der Bildschirm.
      */
      const zeilenachse =
        (spaltenmodus === 'perioden' ? periodenbericht?.zeilen : berichtssatz?.zeilen) ?? []
      const weg = vorfahren(zeilenachse, fund.position)
      if (weg.some((p) => zugeklappt.has(p))) {
        const naechste = new Set(zugeklappt)
        for (const eltern of weg) {
          naechste.delete(eltern)
        }

        gliederungSetzen(naechste)
      }

      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() =>
          document.getElementById(fund.id)?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
        ),
      )
    },
    // `ort.ziel.zu` steht mit in der Liste: Der Sprung muss die Gliederung
    // sehen, wie sie jetzt ist, sonst klappt er eine Position auf, die schon
    // offen ist, und schiebt eine andere zu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [funde, aufriss, spaltenmodus, periodenbericht, berichtssatz, ort.ziel.zu],
  )

  // Wer tippt, will den ersten Treffer sehen und nicht erst die Eingabetaste
  // suchen. Die Kennung der Treffer im Vergleich, nicht die Liste selbst:
  // Sonst spränge die Ansicht bei jedem Neuzeichnen zurück.
  const trefferkette = funde.map((f) => f.id).join('|')
  useEffect(() => {
    if (funde.length > 0) {
      springeZu(0)
    } else {
      setSuchstelle(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trefferkette])

  // Strg+F gehört in dieser Ansicht der Software, nicht dem Browser: Die
  // Browsersuche findet nur, was gerade aufgeklappt ist.
  useEffect(() => {
    function beiTaste(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f' && ort.bereich === 'berichte') {
        e.preventDefault()
        setSucheOffen(true)
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [ort.bereich])

  /*
    Der Anlagenspiegel ebenso — er liest zwölf Läufe und wäre bei jedem
    Rechnen mitgeladen eine Arbeit für eine Ansicht, die die meisten nie
    öffnen.
  */
  useEffect(() => {
    const art = spiegelart[ort.unterpunkt ?? '']
    // **Ohne Kreis die Struktur** (R717): Der Dienst antwortet dann mit der
    // Gliederung und dem Spaltenkatalog, die Werte leer.
    if (!art || !ledger || !periode) {
      return
    }

    let gilt = true
    setSpiegel(null)

    api
      // **Der Bezug entscheidet auch hier** (R752): Wer eine Gesellschaft
      // oder Gruppe gewählt hat, sieht ihre gelieferten Bewegungen.
      .spiegel(kreis, ledger, periode, art, gelieferterBezug)
      .then((s) => {
        if (gilt) {
          setSpiegel(s)
        }
      })
      .catch((f: Error) => setFehler(alsMeldung(f)))

    return () => {
      gilt = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ort.unterpunkt, kreis, ledger, periode, anlagestand])

  /*
    Die Kapitalflussrechnung ebenso — sie liest je Monat einen Lauf und dazu
    den der Vorperiode für den Anfangsbestand.

    Das Raster kommt aus der Spaltenwahl und bedeutet hier etwas anderes als
    dort: Im Periodenbild sagt es, wie viele Spalten nebeneinander stehen; in
    der Kapitalflussrechnung, über welchen Zeitraum gerechnet wird. Dieselbe
    Schaltfläche, dieselbe Bedeutung des Wortes „Jahr" — nur die Wirkung
    unterscheidet sich, weil eine Stromgröße keinen Stichtag kennt.
  */
  useEffect(() => {
    if (ort.unterpunkt !== 'kapitalflussrechnung' || !ledger || !periode) {
      return
    }

    let gilt = true

    // **Im Periodenbild ein anderer Endpunkt**, wie beim Bericht: Je Spalte
    // steht eine eigene Rechnung mit eigenem Anfangsbestand und eigenem
    // Prüfcode. Das ist eine andere Auswertung, nicht dieselbe Ansicht mit
    // anderen Spalten.
    //
    // **Ohne Kreis gibt es kein Periodenbild** (R717): Die Struktur ist
    // dieselbe in jeder Spalte, und zwölf Male dasselbe Schema nebeneinander
    // beantwortet keine Frage. Dann steht das Schema einmal da.
    if (spaltenmodus === 'perioden' && kreis) {
      setKfrPerioden(null)

      api
        .kapitalflussPerioden(
          kreis,
          ledger,
          raster,
          periode,
          kursanpassung,
          von || undefined,
          8,
          kumuliert,
          // Unter einer Gesellschaft je Spalte ihre gelieferten Daten (R759).
          gelieferterBezug,
        )
        .then((k) => {
          if (gilt) {
            setKfrPerioden(k)
          }
        })
        .catch((f: Error) => setFehler(alsMeldung(f)))

      return () => {
        gilt = false
      }
    }

    setKapitalfluss(null)

    api
      .kapitalfluss(
        kreis,
        ledger,
        periode,
        raster,
        kursanpassung,
        von || undefined,
        gelieferterBezug,
      )
      .then((k) => {
        if (gilt) {
          setKapitalfluss(k)
        }
      })
      .catch((f: Error) => setFehler(alsMeldung(f)))

    return () => {
      gilt = false
    }
  }, [ort.unterpunkt, kreis, ledger, periode, von, raster, kursanpassung, spaltenmodus, kfrStand, kumuliert, anlagestand, gelieferterBezug])

  /*
    **Aus welcher Welt der Bericht kommt** (2.13 Abschnitt 14, R1114) — nur für
    einen Kreis gefragt: Die Lieferdaten einer Gesellschaft stehen immer auf dem
    neuesten Stand und brauchen keine Auskunft. Der Zeitraum ist der des
    Berichts: im Periodenbild und in der Kapitalflussrechnung der Schieber,
    sonst der eine Monat.
  */
  // Ein Hauptabruf (7.2 Abschnitt 39, R1211): Die Zeile sagt, wann gerechnet
  // ist und was sich seither geändert hat. Kam sie nicht, steht das da (R1210).
  const weltzeitraum = spaltenmodus === 'perioden' || ort.unterpunkt === 'kapitalflussrechnung'
  const weltabruf = useAbruf<Berichtswelt>(
    ort.bereich !== 'berichte' || gelieferterBezug || !kreis || !ledger || !periode
      ? null
      : () => api.berichtswelt(kreis, ledger, weltzeitraum ? von || periode : periode, periode),
    [ort.bereich, ort.unterpunkt, gelieferterBezug, kreis, ledger, periode, von, spaltenmodus],
  )

  /*
    Der Periodenbericht wird erst geholt, wenn jemand ihn sehen will. Er liest
    je Spalte einen anderen Lauf; das bei jedem Rechnen mitzuladen hieße,
    Arbeit für eine Ansicht zu tun, die die meisten nie öffnen.
  */
  useEffect(() => {
    if (spaltenmodus !== 'perioden' || !ledger || !ort.unterpunkt) {
      return
    }

    const art = ort.unterpunkt === 'guv' ? 'guv' : 'bilanz'
    let gilt = true

    /*
      **Dieselbe Frage, zwei Quellen** (R759, R751) — wie beim Partneraufriß:
      Der gerechnete Lauf hat seine Zahlen festgeschrieben, die gelieferte Sicht
      liest je Spalte das Journal. Kübel, Quartal, Jahr und *kumuliert seit
      Jahresbeginn* rechnen beide Male an derselben Stelle (R754).
    */
    const holen = gelieferterBezug
      ? api.gelieferteperioden(gelieferterBezug, ledger, art, raster, periode, von || undefined, art === 'guv' && kumuliert, plan || null)
      : kreis
        ? api.periodenbericht(kreis, ledger, art, raster, periode, von || undefined, art === 'guv' && kumuliert)
        : null

    if (!holen) {
      return
    }

    holen
      .then((b) => {
        if (gilt) {
          setPeriodenbericht(b)
        }
      })
      .catch((f: Error) => setFehler(alsMeldung(f)))

    // Wer schnell umschaltet, bekommt sonst die Antwort auf die vorletzte
    // Frage, weil sie zufällig später eintrifft.
    return () => {
      gilt = false
    }
  }, [spaltenmodus, raster, kreis, gelieferterBezug, plan, ledger, periode, von, ort.unterpunkt, kumuliert, anlagestand])

  useEffect(() => {
    // Damit Vorleseprogramme und die Silbentrennung des Browsers die richtige
    // Sprache annehmen — auch beim allerersten Zeichnen. Der Titel gehört
    // dazu: Er steht in der Fensterleiste und im Lesezeichen und ist damit
    // Oberfläche wie jede andere.
    document.documentElement.lang = sprache
    document.title = t('anwendung.titel')
  }, [sprache])

  function wechsleSprache(code: string) {
    setzeSprache(code)
    setSprache(code)
  }

  function wechsleZahlenformat(wahl: Zahlenformat) {
    setzeZahlenformat(wahl)
    setZahlenformat(wahl)
    merkeZahlendarstellung({ zahlenformat: wahl })
  }

  /**
   * Beträge mit Vorzeichen oder als Soll und Haben (Auftrag vom 2026-09-27).
   *
   * **Gemerkt wie die Schreibweise der Zahlen**: Wer als Buchhalter arbeitet,
   * stellt das einmal ein und nicht in jeder Sitzung neu.
   */
  function wechsleVorzeichen(wahl: Vorzeichenart) {
    setzeVorzeichenart(wahl)
    setVorzeichen(wahl)
    merkeZahlendarstellung({ vorzeichen: wahl })
  }

  /** Negative Beträge rot oder schwarz (R1015) — gemerkt wie die Vorzeichen. */
  function wechsleMinusfarbe(wahl: Minusfarbe) {
    setzeMinusfarbe(wahl)
    setMinusfarbe(wahl)
    merkeZahlendarstellung({ minusfarbe: wahl })
  }

  /**
   * **Die Zahlendarstellung gehört dem Anwender** (7.2 Abschnitt 43, R1315):
   * Jede Wahl geht an den Dienst, damit sie an jedem Rechner gilt. Der
   * Browser merkt sie sich weiter — so steht beim nächsten Start die
   * richtige Schreibweise da, bevor der Dienst geantwortet hat.
   */
  function merkeZahlendarstellung(aenderung: Partial<Zahlenstand>) {
    const neu = { vorzeichen, minusfarbe, zahlenformat, ...aenderung }
    imHintergrund(() => api.zahlendarstellungSetzen(neu)).catch(() => {
      // Kam der Dienst nicht, gilt die Wahl in diesem Browser weiter.
    })
  }

  /*
    **Beim Anmelden gilt die eigene Zahlendarstellung** (R1315); hat der
    Anwender noch keine, wird die des Browsers seine (R1316).
  */
  useEffect(() => {
    if (!angemeldet) {
      return
    }

    let gilt = true
    const browser: Zahlenstand = {
      vorzeichen: aktuelleVorzeichenart(),
      minusfarbe: aktuelleMinusfarbe(),
      zahlenformat: aktuellesZahlenformat(),
    }

    imHintergrund(() => eigeneZahlendarstellung(browser))
      .then((stand) => {
        if (!gilt) {
          return
        }

        setzeVorzeichenart(stand.vorzeichen)
        setVorzeichen(stand.vorzeichen)
        setzeMinusfarbe(stand.minusfarbe)
        setMinusfarbe(stand.minusfarbe)
        setzeZahlenformat(stand.zahlenformat)
        setZahlenformat(stand.zahlenformat)
      })
      .catch(() => {
        // Ohne Antwort bleibt, was der Browser sich gemerkt hat.
      })

    return () => {
      gilt = false
    }
  }, [angemeldet?.anwender])

  useEffect(() => {
    function beiAdresswechsel() {
      setOrt(leseAdresse())
    }

    window.addEventListener('hashchange', beiAdresswechsel)
    return () => window.removeEventListener('hashchange', beiAdresswechsel)
  }, [])

  /**
   * Der Sprung aus dem Begleiter (7.1 Abschnitt 10, R547).
   *
   * **Jedes Fenster ausser dem Begleiter selbst springt** — und nur wer
   * springt, meldet sich zurueck, damit der Begleiter weiss, ob jemand da
   * war. Das ist eine Handlung, keine Kopplung: Kreis, Bewertungsebene und Zeitraum
   * dieses Fensters bleiben, wie sie sind (R530).
   */
  useEffect(
    () =>
      sprungEmpfangen((ziel) => {
        const hier = leseAdresse()
        if (hier.bereich === 'hilfe' && hier.unterpunkt === 'begleiter') return false
        window.location.hash = ziel.unterpunkt ? `#/${ziel.bereich}/${ziel.unterpunkt}` : `#/${ziel.bereich}`
        setOrt(leseAdresse())
        window.focus()
        return true
      }),
    [],
  )

  /**
   * Was ein Doppelklick auf eine Zahl auslöst (R204/R205).
   *
   * Die Zelle bringt Position, Konto, Gesellschaft und **Spalte** mit; Kreis,
   * Bewertungsebene und Periode kommen aus der Kontextzeile. Eine Zeile aus dem
   * Periodenbild bringt ihre eigene Periode mit — sonst führte ein Klick auf
   * die Märzspalte zum Nachweis des Dezembers.
   */
  const rufeNachweis = useCallback(
    (zeiger: Zellenzeiger) => {
      // **Ohne Lauf zählt der Bezug** (R771): In der gelieferten Sicht gibt es
      // keinen Kreis, und auf ihm zu bestehen hieße, den Doppelklick genau
      // dort abzuschalten, wo der Anwender seine eigene Lieferung ansieht.
      if ((!kreis && !gelieferterBezug) || !ledger || !periode) {
        return
      }

      setNachweis({
        kreis,
        bezug: gelieferterBezug,
        ledger,
        art: ort.unterpunkt === 'guv' ? 'guv' : 'bilanz',
        periode: zeiger.periode ?? periode,
        position: zeiger.position,
        positionsname: zeiger.positionsname,
        konto: zeiger.konto,
        einheit: zeiger.einheit,
        spalte: zeiger.spalte,
        weitere: zeiger.weitere,
        spiegelblock: zeiger.spiegelblock,
        spiegelspalte: zeiger.spiegelspalte,
      })
    },
    [kreis, gelieferterBezug, ledger, periode, ort.unterpunkt],
  )

  /*
    **Wann ein Doppelklick etwas auslöst — einmal beantwortet** (R771).

    Bis zum 2026-09-25 stand an jeder Wertzelle einzeln `lauf ? … : undefined`.
    Damit fiel der Nachweis in der gelieferten Sicht stillschweigend aus, und er
    fiele bei jeder weiteren Quelle wieder aus, bis jemand die nächste Stelle
    findet. Die Frage ist dieselbe: Gibt es etwas, woraus sich die Zahl
    erklären läßt?
  */
  const nachweisruf = lauf || gelieferterBezug ? rufeNachweis : undefined

  /**
   * Buchen aus dem Bericht heraus (R385, R387).
   *
   * **Was die Zelle weiß, steht vorbelegt** — und zwar aus derselben Quelle
   * wie der Wertenachweis (R213): Bewertungsebene und Periode aus der Kontextzeile,
   * Konto und Gesellschaft aus dem Aufriss, die aufgerissene Dimension aus
   * `weitere`. Alles änderbar; vorbelegt heißt nicht festgelegt.
   *
   * **Die eine Ausnahme ist die Konsolidierungsspalte.** Dort stehen
   * gerechnete Werte, und eine Handbuchung auf sie gibt es nicht — sie gehört
   * in die Bewertungsebene, aus der gerechnet wird. Der Punkt bleibt anklickbar und
   * sagt, warum nichts vorbelegt ist.
   */
  const rufeBuchung = useCallback(
    (zeiger: Zellenzeiger) => {
      if (!kreis || !ledger || !periode) {
        return
      }

      const gerechnet = zeiger.spalte !== 'hb2' && zeiger.spalte !== 'konzern'

      setBuchung({
        // **Ohne Gesellschaft aus der Zelle keine Vorbelegung der Gesellschaft** —
        // die Maske nimmt dann die erste des Konzerns, und der Anwender
        // wechselt sie. Eine geratene Gesellschaft wäre schlimmer.
        einheit: zeiger.einheit ?? ersteEinheit(),
        ledger,
        periode: zeiger.periode ?? periode,
        kreis,
        konto: gerechnet ? null : zeiger.konto,
        weitere: gerechnet ? undefined : zeiger.weitere,
        ausKonsolidierungsspalte: gerechnet,
      })
    },
    [kreis, ledger, periode],
  )

  /**
   * Eine Struktur unter der angeklickten Zeile anlegen — die fünfte
   * Menügruppe (R494, 7.4 Abschnitt 14).
   *
   * **Die Periode kommt aus der Kontextzeile und ist in der Maske änderbar**
   * (R499): Sie ist die kleinste Struktureinheit (R2), und wer im September
   * sieht, dass ab Januar eine Position fehlt, muss das sagen können, ohne den
   * Bericht zu wechseln.
   *
   * Der Positionsplan steht nicht dabei: Er ist der des Laufs, und die Maske
   * liest ihn aus der Vorschau — eine Planauswahl über einem Bericht wäre ein
   * zweiter Weg zu einer Aussage, die der Bericht schon getroffen hat.
   */
  const rufeAnlage = useCallback(
    (art: 'konto' | 'position', position: string, positionsname: string) => {
      if (!periode) {
        return
      }

      setAnlage({ art, position, positionsname, periode })
    },
    [periode],
  )

  /**
   * Eine Zeile unter ihrer Summe in der Kapitalflussrechnung anlegen
   * (R494, R507).
   *
   * **Die Periode reist mit und wird nicht gebraucht.** Der Zeilenkatalog
   * traegt keinen Gueltigkeitszeitraum; die Maske zeigt das Feld dort nicht.
   * Sie einheitlich mitzugeben kostet nichts und haelt den Zeiger gleich —
   * ein zweiter Zeigertyp fuer ein Feld weniger waere die teurere Haelfte.
   */
  const rufeKfrAnlage = useCallback(
    (zeile: string, name: string, kfrArt: 'unter' | 'abschnitt', summenzeile = false) => {
      if (!periode) {
        return
      }

      setAnlage({ art: 'kfrzeile', position: zeile, positionsname: name, periode, kfrArt, summenzeile })
    },
    [periode],
  )
  /**
   * Der Nachweis aus einer Zelle der Kapitalflussrechnung (R213).
   *
   * Die Zelle bringt Zeile, Konto, Gesellschaft und **Spalte** mit; Kreis,
   * Bewertungsebene, Stichtag, Raster und die Stellung der Stichtagskursanpassung
   * kommen aus der Kontextzeile. **Eine Spalte des Periodenbildes bringt
   * ihren eigenen Zeitraum mit** — sonst erklärte ein Klick auf das zweite
   * Quartal das ganze Jahr (R269).
   */
  const rufeKfrNachweis = useCallback(
    (zeiger: Kfrzellenzeiger) => {
      // **Ohne Lauf zählt der Bezug** (R771) — dieselbe Weiche wie beim
      // Bilanznachweis. In der gelieferten Sicht gibt es keinen Kreis.
      if ((!kreis && !gelieferterBezug) || !ledger || !periode) {
        return
      }

      setKfrNachweis({
        kreis,
        bezug: gelieferterBezug,
        ledger,
        bis: zeiger.bis ?? periode,
        von: zeiger.von ?? (von || null),
        raster,
        waehrungsdelta: kursanpassung,
        zeile: zeiger.zeile,
        code: zeiger.code,
        zeilenname: zeiger.zeilenname,
        einheit: zeiger.einheit,
        konto: zeiger.konto,
        spalte: zeiger.spalte,
        weitere: zeiger.weitere,
      })
    },
    [kreis, gelieferterBezug, ledger, periode, von, raster, kursanpassung],
  )

  /*
    **Wann ein Doppelklick in der Kapitalflußrechnung etwas auslöst** (R771).

    Dieselbe Frage wie beim Bilanznachweis und deshalb dieselbe Bauform: einmal
    beantwortet, nicht an jeder Wertzelle. Bis zum 2026-09-26 stand hier an drei
    Stellen einzeln `lauf ? … : undefined`, und in der gelieferten Sicht
    geschah nichts.
  */
  const kfrNachweisruf = lauf || gelieferterBezug ? rufeKfrNachweis : undefined

  const gehe = useCallback(
    (bereich: string, unterpunkt?: string, ziel?: Record<string, string | undefined>) => {
      const eintraege = Object.entries(ziel ?? {}).filter(
        (paar): paar is [string, string] => paar[1] !== undefined && paar[1] !== '',
      )
      const frage = eintraege.length > 0 ? `?${new URLSearchParams(eintraege)}` : ''

      window.location.hash = unterpunkt
        ? `#/${bereich}/${unterpunkt}${frage}`
        : `#/${bereich}${frage}`
      setOrt(leseAdresse())
    },
    [],
  )

  /**
   * Die Adresse eines Leisteneintrags — **mit dem Kontext dieses Fensters**
   * (7.2 Abschnitt 3c, R529/R530).
   *
   * Sie ist dieselbe Adresse, die ein Klick ansteuert (R156); dazu kommen
   * Kreis, Bewertungsebene und Zeitraum. Wer aus einem Bericht heraus den Nachweis
   * aufmacht, findet dort denselben Abschluss — und ab dann sind die beiden
   * Fenster voneinander unabhängig.
   */
  const leistenadresse = useCallback(
    (bereich: string, unterpunkt?: string) => {
      const frage = new URLSearchParams()
      // **Das Element ist der Bezug, der Kreis die Rückfrage** (R715, R716).
      if (element) frage.set('o', element)
      if (kreis) frage.set('k', kreis)
      if (ledger) frage.set('l', ledger)
      if (von) frage.set('v', String(von))
      if (periode) frage.set('b', String(periode))
      frage.set('r', raster)

      const pfad = unterpunkt ? `${bereich}/${unterpunkt}` : bereich
      return `#/${pfad}?${frage}`
    },
    [element, kreis, ledger, von, periode, raster],
  )

  /*
    **Wer auf einer Adresse steht, die es für ihn nicht gibt, wird geführt**
    (R144/R155/R360) — und bekommt keine Störungsmeldung.

    Der Fall ist nicht ausgedacht: Die Adresse überlebt den Anmeldewechsel. Wer
    als Administrator in *Anwender und Rechte* stand, sich abmeldet und als
    Prüfer zurückkommt, steht dort weiter — und die API antwortet ihm, als
    gäbe es den Bereich nicht. Das ist richtig; **„Die Anlage antwortet nicht"
    wäre es nicht.**
  */
  useEffect(() => {
    if (!angemeldet) {
      return
    }

    const dieser = findeBereich(ort.bereich)
    if (dieser.verb && !angemeldet.rechte.includes(dieser.verb)) {
      gehe('arbeitsliste')
    }
  }, [angemeldet, ort.bereich, gehe])

  // Der Zusammenklapp-Zustand wird gemerkt wie der Aufklappzustand des
  // Positionsbaums (L2): Der Vielnutzer klappt einmal zu und behält den Platz.
  function klappen(eng: boolean) {
    setEingeklappt(eng)
    window.localStorage.setItem(LEISTE_GEMERKT, eng ? 'eng' : 'weit')
  }

  async function freigeben(begruendungen: Record<string, string>) {
    if (!lauf) {
      return
    }

    setGibtFrei(true)
    try {
      setUrteil(await api.freigeben(lauf.laufId, null, begruendungen))
    } finally {
      setGibtFrei(false)
    }
  }

  useEffect(() => {
    let gilt = true
    api
      .auswahl()
      .then((a) => {
        if (!gilt) {
          return
        }
        setAuswahl(a)
        /*
          **Der Kontext des Ausgangsfensters schlägt die Vorbelegung** (R530).
          Nur was tatsächlich zur Auswahl steht, wird übernommen: Ein Kreis aus
          einer Adresse, die dieser Anwender nicht sehen darf, ist kein Kreis,
          sondern ein Tippfehler — dann gilt wieder der erste der Liste.
        */
        // **Aus `k` ist `o` geworden** (R716): Der Bericht hängt an einem
        // Organisationselement. Eine alte Adresse mit Kreisangabe verliert
        // damit ihre Wirkung — der Preis ist im Konzept benannt.
        const elementWunsch = (a.elemente ?? []).find(
          (e) => e.schluessel === KONTEXT_BEIM_OEFFNEN.o,
        )
        const ledgerWunsch = a.ledger.find((l) => l.schluessel === KONTEXT_BEIM_OEFFNEN.l)
        // **Danach das Gemerkte, wenn es noch zur Wahl steht** (R1363, R1364),
        // und erst dann der erste Eintrag der Liste.
        setElement(
          (gemerkt) =>
            elementWunsch?.schluessel ??
            ((a.elemente ?? []).some((e) => e.schluessel === gemerkt) ? gemerkt : undefined) ??
            (a.elemente ?? [])[0]?.schluessel ??
            '',
        )
        // Der Kreis aus der Adresse bleibt die **Rückfrage** (R715) und nicht
        // die Auswahl: Er sagt, in welchem von mehreren gelesen wird. Ohne ihn
        // gilt die gemerkte Rückfrage — ob sie passt, prüft die Ableitung des Kreises.
        if (KONTEXT_BEIM_OEFFNEN.k) {
          setKreiswunsch(KONTEXT_BEIM_OEFFNEN.k)
        }
        setLedger(
          (gemerkt) =>
            ledgerWunsch?.schluessel ??
            (a.ledger.some((l) => l.schluessel === gemerkt) ? gemerkt : undefined) ??
            a.ledger[0]?.schluessel ??
            '',
        )
        setPeriode(a.perioden[0]?.periode ?? 0)
      })
      .catch((f: Error) => gilt && setFehler(alsMeldung(f)))

    // Der Stammdatenkatalog beschriftet, was der Rechenkern als Schlüssel
    // schickt. Er wird einmal geholt und nicht bei jedem Sprachwechsel:
    // Genau das ist der Punkt.
    // **Alle drei im Hintergrund** (7.2 Abschnitt 30, R1007): Sie melden
    // keine Störung und versuchen es nach wenigen Sekunden einmal erneut.
    imHintergrund(() => api.positionsplaene())
      .then((p) => gilt && setPlaene(p))
      .catch(() => {
        // Ohne die Liste bleibt die Vorgabe — ein Bericht kommt trotzdem.
      })

    // **Bis das Verzeichnis da ist, steht an jedem Namen „…“** (R1661) — nicht
    // der Code. Am 2026-10-12 sah der Wächter so „KRS-000016-18“ auf der
    // Seite Konzernabschlüsse.
    katalogLaedt()
    imHintergrund(() => api.stammdatenkatalog())
      .then((k) => {
        if (!gilt) return
        setzeKatalog(k)
        setKatalogstand((n) => n + 1)
      })
      .catch(() => {
        // Ohne Katalog stehen Kontokennungen statt Bezeichnungen — unschön,
        // aber kein Grund für ein Fenster (R1007).
        katalogFehlt()
        if (gilt) setKatalogstand((n) => n + 1)
      })

    // **Nach jedem Speichern kommt das Verzeichnis neu** (7.2 Abschnitt 20,
    // R1736) — ohne „…“: Bis es da ist, bleibt der bisherige Eintrag stehen.
    const beende = beobachteSpeichern(() => {
      imHintergrund(() => api.stammdatenkatalog())
        .then((k) => {
          if (!gilt) return
          setzeKatalog(k)
          setKatalogstand((n) => n + 1)
        })
        .catch(() => {
          // Kommt es nicht, bleibt das bisherige stehen (R1007).
        })
    })

    return () => {
      gilt = false
      beende()
    }
  }, [])

  /*
    **Die Partnercodes kommen aus der gewöhnlichen Stammdatenliste** (R490).
    Sie beschriften die Spalten des Partnerbilds und geben ihre Reihenfolge
    vor (R493); ohne sie gilt weiterhin die Einheit dahinter, und der
    Bildschirm bleibt lesbar.

    **Neu geholt mit jedem Stand der Stammdaten und jedem Wechsel des
    Bereichs** (R1702): Bis zum 2026-10-14 einmal beim Öffnen — eine unter
    *Stammdaten* geänderte Reihenfolge der Partner sah das Partnerbild erst
    nach dem Neuladen der Seite.
  */
  useEffect(() => {
    let gilt = true
    imHintergrund(() => api.stammdatenliste('partnercode'))
      .then(
        (liste) =>
          gilt &&
          setzePartnercodes(
            liste.eintraege.map((e) => ({
              schluessel: e.schluessel,
              code: e.code,
              namen: e.namen,
              rang: Number(e.merkmale[2]) || 0,
            })),
          ),
      )
      .catch(() => {
        // Ohne die Liste gilt die Einheit dahinter (R1007).
      })

    return () => {
      gilt = false
    }
  }, [anlagestand, ort.bereich])

  /**
   * Einen Lauf in die Ansicht holen — die drei Sichten desselben Laufs
   * zusammen, weil der Anwender zwischen ihnen wechselt und dabei nicht
   * warten soll.
   */
  const ladeLauf = useCallback(async (antwort: Rechenantwort, gliederung = plan) => {
    setLauf(antwort)

    const [b, g, v] = await Promise.all([
      api.bericht(antwort.laufId, 'bilanz', gliederung || undefined),
      api.bericht(antwort.laufId, 'guv', gliederung || undefined),
      api.vorgaenge(antwort.laufId),
    ])

    setBilanz(b)
    setGuv(g)
    setVorgaenge(v)
    setBerichtausfall(false)

    const [p, m, u, q] = await Promise.all([
      api.nachweispruefung(),
      api.messung(),
      api.freigabepruefung(antwort.laufId),
      // Im Hintergrund (R1007): ohne Datenquellen bleibt die Auswahl leer.
      imHintergrund(() => api.datenquellen()).catch(() => null),
    ])
    setPruefung(p)
    setMessung(m)
    setUrteil(u)
    setDatenquellen(q)
  }, [])

  /*
    Wer die Periode wechselt, will Zahlen sehen — keinen Knopf.

    Gezeigt wird der Lauf, der für die Auswahl gilt: der freigegebene, sonst
    der jüngste (R169). **Gerechnet wird dabei nicht.** Das ist der
    Unterschied, auf den es ankommt: Ansehen ist eine Frage, Rechnen schreibt
    einen Lauf fest und gehört deshalb an einen ausdrücklichen Griff. Solange
    nur gelesen wird, ist der Knopf ein Umweg.
  */
  useEffect(() => {
    /*
      **Der Bezug entscheidet, welche Sicht gilt** (R752).

      Zeigt er auf eine Gesellschaft oder eine Gruppe, kommen die
      GELIEFERTEN Zahlen — unmittelbar aus dem Journal, ohne Lauf und ohne
      Konsolidierung (R751). Was importiert ist, ist sofort sichtbar; einen
      Lauf dafür zu starten wäre ein Umweg, der nichts beantwortet.

      Zeigt er auf einen Kreis, bleibt alles wie bisher: Dort entstehen die
      Zahlen aus Konsolidierungsschritten, und die gibt es nur im Lauf.
    */
    const gelieferteSicht = gewaehltesElement !== undefined && gewaehltesElement?.art !== 'kreis'

    if (gelieferteSicht && element && ledger && periode) {
      let gilt = true
      setFehler(null)

      Promise.all([
        api.gelieferteSicht(element, ledger, periode, 'bilanz', plan || undefined),
        api.gelieferteSicht(element, ledger, periode, 'guv', plan || undefined),
      ])
        .then(([b, g]) => {
          if (!gilt) {
            return
          }

          // **Kein Lauf, also auch keine Laufangaben** (R751): weder
          // Vorgänge noch Freigabeurteil noch Prüfcode. Was dort stünde,
          // wäre die Behauptung, hier sei gerechnet worden.
          setLauf(null)
          setVorgaenge([])
          setUrteil(null)
          setBilanz(b)
          setGuv(g)
          setBerichtausfall(false)
        })
        .catch((f: Error) => {
          if (gilt) {
            setFehler(alsMeldung(f))
            // Ausgefallen, nicht leer (R1210) — siehe `berichtausfall`.
            setBerichtausfall(true)
          }
        })

      return () => {
        gilt = false
      }
    }

    if (!kreis || !ledger || !periode) {
      return
    }

    let gilt = true
    setFehler(null)

    api
      .aktuellerLauf(kreis, ledger, periode)
      .then(async (antwort) => {
        if (!gilt) {
          return
        }

        if (antwort === null) {
          // Keine Rechnung zu dieser Auswahl: Die Ansicht sagt es selbst,
          // statt die Zahlen der vorigen Periode stehen zu lassen.
          setLauf(null)
          setBilanz(null)
          setGuv(null)
          setVorgaenge([])
          setUrteil(null)
          setBerichtausfall(false)
          return
        }

        await ladeLauf(antwort)
      })
      .catch((f: Error) => {
        if (gilt) {
          setFehler(alsMeldung(f))
          // **Ausgefallen, nicht die vorige Auswahl** (7.2 Abschnitt 39, R1210):
          // Bis zum 2026-10-06 blieben hier die Zahlen des vorigen Monats
          // stehen, und nur die Meldung sagte, dass etwas fehlt.
          setBerichtausfall(true)
        }
      })

    // Wer schnell durch die Perioden blättert, bekommt sonst die Antwort auf
    // die vorletzte Frage, weil sie zufällig später eintrifft.
    return () => {
      gilt = false
    }
  }, [kreis, ledger, periode, ladeLauf, anlagestand, element, gewaehltesElement, plan, berichtsversuch])

  /*
    **Die Arbeitsliste überwacht, was der Anwender festlegt** (7.1 Abschnitt
    12, R1512): geholt unabhängig von Kreis, Bewertungsebene und Periode über
    den Berichten — die Kopfzeile wirkt nicht mehr auf die Liste und nicht auf
    die Zahl an ihrem Symbol. Neu geholt beim Öffnen der Startseite und nach
    jeder Änderung an den historischen Kursen; die Fälle legt der Server ab.
  */
  const [ueberwachung, setUeberwachung] = useState<Ueberwachungsstand | null>(null)
  const [ueberwachungAusgefallen, setUeberwachungAusgefallen] = useState(false)
  const aufStartseite = ort.bereich === 'arbeitsliste'
  useEffect(() => {
    let gilt = true
    api
      .ueberwachung()
      .then((s) => {
        if (gilt) {
          setUeberwachung(s)
          setUeberwachungAusgefallen(false)
        }
      })
      .catch(() => gilt && setUeberwachungAusgefallen(true))
    return () => {
      gilt = false
    }
  }, [anlagestand, historischVersuch, aufStartseite])

  /*
    **Der Klick aus dem Blatt öffnet die Zielseite mit der Gesellschaft und dem
    Zeitraum** (R1521): Er stellt die Auswahl über den Berichten auf die
    Gesellschaft — oder den Konzern —, ihre Bewertungsebene und den Zeitraum
    des Blatts, dann geht er dorthin. Steht die Gesellschaft nicht im Baum,
    bleibt die Auswahl, wie sie ist.
  */
  const blattsprung = (s: Blattsprung) => {
    const bezug = s.einheit ?? s.kreis
    if (bezug && auswahl?.elemente.some((e) => e.schluessel === `ORG-${bezug}`)) {
      setElement(`ORG-${bezug}`)
    }
    if (s.kreis) {
      setKreiswunsch(s.kreis)
    }
    if (s.ledger) {
      setLedger(s.ledger)
    }
    setVon(s.von)
    setPeriode(s.bis)
    gehe(s.ziel.bereich, s.ziel.unterpunkt, s.ziel.auswahl)
  }

  // Der Kreis heißt, wie der Namensgeber ihn nennt (R1660, R838).
  const kreisname = kreis ? kreisnameImKatalog(kreis) : t('kontext.kreis.rueckfall')

  /*
    **Der Konzernabschluss, um den es geht** (7.1 Abschnitt 13, R1527, R1528):
    Sein Name steht in der Überschrift einer Meldung, sein Schlüssel im
    Verweis — „Konzernabschlüsse" öffnet die Rückfrage „Konzernabschluss
    rechnen" für ihn. Im Hintergrund: Fällt der Abruf aus, steht der Name des
    Kreises da, und der Verweis öffnet die Liste ohne Rückfrage.
  */
  const konzernabschlussliste = useHintergrundabruf(() => api.konzernabschluesse(), [anlagestand], KEINE_KONZERNABSCHLUESSE)
  const gemeinterAbschluss = konzernabschlussliste.find(
    (a) => a.kreis === kreis && a.ledger === ledger && a.vonPeriode <= periode && periode <= a.bisPeriode,
  )
  const meldungskontext: Meldungskontext = {
    abschluss: gemeinterAbschluss
      ? { schluessel: gemeinterAbschluss.schluessel, name: beschriftung(gemeinterAbschluss.namen) || kreisname }
      : { schluessel: null, name: kreisname },
  }

  // Unter dem Blatt, was die ganze Anwendung betrifft (R1519) — nur bei einer
  // Störung und nur für den, der verwaltet (R1575).
  const zeilen = anwendungszeilen(pruefung, messung, angemeldet?.rechte.includes('verwalten') ?? false)
  const bereich = findeBereich(ort.bereich)

  /*
    **Die Anmeldung ist das Tor** (8.1, R355/R362) — und zwar in dieser
    Reihenfolge:

      1. Solange niemand gefragt wurde, steht nichts da. Ein Bildschirm, der
         erst erscheint und dann von der Anmeldung verdeckt wird, sieht aus
         wie ein Fehler.
      2. Ohne Sitzung: die Anmeldung. **Nicht** eine leere Anwendung mit dem
         Hinweis, man möge sich anmelden — R144 heißt führen, nicht andeuten.
      3. Mit Erstkennwort: der Wechsel, und sonst nichts (R362).
  */
  if (!gefragt) {
    return <div className="anmeldehof" />
  }

  if (ichAusgefallen && !angemeldet) {
    return (
      <div className="anmeldehof">
        <Abrufausfall abruf={{ erneut: frageWerIchBin }} />
      </div>
    )
  }

  if (!angemeldet) {
    return <Anmeldemaske aufAnmeldung={frageWerIchBin} />
  }

  if (angemeldet.mussKennwortWechseln) {
    return (
      <Kennwortmaske
        name={angemeldet.name}
        aufGewechselt={frageWerIchBin}
        aufAbmelden={() => setAngemeldet(null)}
      />
    )
  }

  /*
    **Eine Tafel ist ein Bildschirm, der im Wesentlichen eine Tabelle ist**
    (R412): Die Seite steht, die Tabelle rollt in sich -- nur so bleibt der
    Tabellenkopf stehen, und nur so sieht man bei Zeile achtzig noch, welche
    Gesellschaft in welcher Spalte steht.

    **Welcher Bildschirm eine ist, sagt der Bildschirm** (`bereiche.tsx`) und
    nicht eine Liste von Ausnahmen hier -- dieselbe Bauweise wie bei der
    Kontextzeile (R330). Sonst waere der naechste Bildschirm die erste Stelle,
    an der beides auseinanderlaeuft.

    Der Schalter sitzt am aeussersten Kasten, damit die Kette aus Flex-Kaesten
    ohne :has() auskommt.
  */
  const tafel = istTafel(ort.bereich, ort.unterpunkt)

  return (
    <Anzeigeoptionenkontext value={anzeigeoptionen}>
    <Gliederungskontext value={{ zu: zugeklappt, umschalten: gliederungUmschalten, setzen: gliederungSetzen }}>
    {/* **Was der Anwender nicht darf, ist auf keiner Seite da** (8.1 Abschnitt 7, R1180). */}
    <Rechtekontext value={rechtelage}>
    <div className={tafel ? 'anwendung tafel' : 'anwendung'}>
      <Seitenleiste
        aktiv={ort.bereich}
        aktiverUnterpunkt={ort.unterpunkt}
        eingeklappt={eingeklappt}
        aufAuswahl={gehe}
        aufKlappen={klappen}
        anhaenger={{ arbeitsliste: offeneFaelle(ueberwachung) + offenePunkte(zeilen) }}
        rechte={angemeldet.rechte}
        angemeldetAls={t('anmeldung.angemeldetals', { name: angemeldet.name })}
        abgeschaltet={angemeldet.istPlatzhalter}
        aufProfil={() => setProfilOffen(true)}
        aufAbmelden={async () => {
          // Abgemeldet wird auch, wenn der Dienst fort ist — kein Fenster dafür.
          await api.abmelden().catch((f) => void still(f))
          // Die Liste des Begleiters gehoert dem, der sie angelegt hat (R548).
          begleitlisteSchreiben(null)
          setAngemeldet(null)
        }}
        adresse={leistenadresse}
      />
      {profilOffen && (
        <Profil
          aufSchliessen={() => setProfilOffen(false)}
          zahlen={{
            zahlenformat,
            vorzeichen,
            minusfarbe,
            aufZahlenformat: wechsleZahlenformat,
            aufVorzeichen: wechsleVorzeichen,
            aufMinusfarbe: wechsleMinusfarbe,
          }}
        />
      )}

      <div className="saeule">
        {/*
          **Die Kontextzeile steht, wo sie wirkt** (R330) — dieselbe
          Entscheidung wie beim Zeitwert (R293) und aus demselben
          Grund: Drei Auswahlfelder, die nichts steuern, sind nicht bloß
          überflüssig, sie behaupten etwas. Wer im Hilfebereich einen
          Konsolidierungskreis einstellen kann, darf annehmen, dass die Hilfe
          für diesen Kreis eine andere ist.

          Welcher Ort sie braucht, sagt der Ort selbst (`bereiche.tsx`) und
          nicht eine zweite Liste hier: Sonst wäre der nächste Bildschirm die
          erste Stelle, an der beides auseinanderläuft.
        */}
        {brauchtKontext(ort.bereich, ort.unterpunkt) && (
          <header className="kontextzeile">
            {/*
              **Woran der Bericht hängt** (2.3 Abschnitt 10, R712–R716). Hier
              stand bis zum 2026-09-24 der Konsolidierungskreis, und damit war
              die Frage „wie sieht die Bilanz dieser einen Gesellschaft aus"
              gar nicht zu stellen. Der Kreis ist eine der Arten geblieben.
            */}
            <div className="feld">
              <label htmlFor="element">{t('kontext.element')}</label>
              <Auswahlfeld
                id="element"
                wert={element}
                eintraege={elementwahl}
                aufWahl={setElement}
                fussnote={aufnahmehinweis}
              />
            </div>

            {/*
              **Die Rückfrage, und nur wo es eine gibt** (R715, R293): Liegt
              das Element in mehreren Kreisen, ist zu sagen, in welchem
              gelesen wird — seine Handelsbilanz II kann je Kreis verschieden
              ausfallen. Bei einem einzigen steht hier nichts.
            */}
            {kreisrueckfrage.length > 1 && (
              <div className="feld">
                <label htmlFor="kreis">{t('kontext.kreis.rueckfrage')}</label>
                <Auswahlfeld
                  id="kreis"
                  wert={kreis}
                  eintraege={kreisrueckfrage}
                  aufWahl={setKreiswunsch}
                />
              </div>
            )}

            <div className="feld">
              <label htmlFor="ledger">{t('kontext.ledger')}</label>
              <Auswahlfeld id="ledger" wert={ledger} eintraege={ledgerwahl} aufWahl={setLedger} />
            </div>

            {/*
              Der Zeitraum statt einer einzelnen Periode (7.3 Abschnitt 2g):
              ein Schieber von–bis in Rastereinheiten, das Raster daneben. In
              Bildern ohne Zeitverlauf zählt das rechte Ende als Stichtag.
            */}
            <Zeitschieber
              laufperioden={laufperioden}
              von={von || periode}
              bis={periode}
              raster={raster}
              aufZeitraum={waehleZeitraum}
              aufRaster={setRaster}
            />

            {/* Am gemerkten Zeitraum, wenn es neuere Zahlen gibt (R1365). */}
            {juengsteperiode !== null && (
              <button type="button" className="ortsverweis" onClick={zeigeJuengste}>
                {t('zeitraum.neuere', {
                  bis: einheitenname(einheitVon(juengsteperiode, raster), raster),
                })}
              </button>
            )}

            {/*
              **Hier stand bis zum 2026-09-10 „Abschluss rechnen"** — der Knopf
              ist geparkt, bis der Konsolidierungsworkflow ihn braucht (2.13,
              R294/R295).

              Der Grund ist nicht der Platz, sondern die Frage, die er stellte:
              Wer einen Bericht öffnet, will Zahlen ansehen. Dass dahinter noch
              kein Lauf steht, ist eine Auskunft über den Bedienzustand der
              Anwendung und nicht über den Abschluss — die Daten waren die ganze
              Zeit da. Fehlende und durch neue Daten überholte Läufe holt jetzt
              die Laufvorsorge im Berichtsdienst nach, ohne jemanden zu fragen.
            */}

            {/*
              Der Zeitwert steht im fachlichen Streifen, weil er zur Auswertung
              gehört — anders als Sprache und Zahlenformat, die unten in der
              Fußzeile sitzen: Eine Einstellung, die ein Anwender zweimal im
              Jahr anfasst, darf oben keinen Platz belegen.

              **Er steht nur, wo er wirkt** (R293, R426) — im Spaltenbild
              *Perioden* von Bilanz, GuV und Kapitalflussrechnung. Eine einzelne
              Spalte hat keine Zeitreihe: Weder lässt sich darin etwas
              kumulieren noch gegen eine Vorspalte rechnen, die es nicht gibt.
              Dasselbe gilt für die Spiegel, den Abschluss und die Nachweise.
              **Die Wahl selbst bleibt stehen**: Wer aus dem Periodenbild kurz
              woandershin sieht, findet sie bei der Rückkehr, wie er sie
              verlassen hat.
            */}
            {/*
              **Die Anzeigeoptionen stehen, wo eine Gliederung steht** -- in
              Bilanz, GuV und den drei Spiegeln. Sie aendern keine Zahl,
              sondern nehmen Zeilen weg, in denen keine steht (7.3 Abschnitt
              11). In der Kapitalflussrechnung gibt es sie nicht: Der DRS 21
              hat einen festen Zeilensatz, und der daempft seine leeren Zeilen
              seit jeher selbst.
            */}
            {ort.bereich === 'berichte' &&
              (['bilanz', 'guv', 'spiegel', 'eigenkapitalspiegel', 'kreditspiegel'].includes(
                ort.unterpunkt ?? '',
              ) ||
                // In der Kapitalflussrechnung nur im Periodenbild — dort
                // gibt es Zwischensummen, Nullzeilen nicht (R1018).
                (ort.unterpunkt === 'kapitalflussrechnung' && spaltenmodus === 'perioden')) && (
                <Anzeigeoptionenknopf
                  optionen={anzeigeoptionen}
                  nullzeilen={ort.unterpunkt !== 'kapitalflussrechnung'}
                  mitZwischensummen={
                    spaltenmodus === 'perioden' &&
                    ['bilanz', 'guv', 'kapitalflussrechnung'].includes(ort.unterpunkt ?? '')
                  }
                  aufZwischensumme={zwischensummeUmschalten}
                  zusatzachsen={achsen}
                  // **Nur die Spiegel haben Spalten aus Bewegungsarten**
                  // (R707): Bilanz und GuV haben Periodenspalten, und ein
                  // Haken, der dort nichts tun kann, ist eine Frage ohne
                  // Antwort.
                  mitBewegungsarten={['spiegel', 'eigenkapitalspiegel', 'kreditspiegel'].includes(
                    ort.unterpunkt ?? '',
                  )}
                  aufWahl={nullzeilenUmschalten}
                />
              )}

            {/*
              **Die Anzeigewährung steht neben den Anzeigeoptionen** (2.12
              Abschnitt 3.21, R993) — über jedem Bericht mit Zahlen in einer
              Währung, die Kapitalflussrechnung eingeschlossen.
            */}
            {ort.bereich === 'berichte' &&
              ['bilanz', 'guv', 'spiegel', 'eigenkapitalspiegel', 'kreditspiegel', 'kapitalflussrechnung'].includes(
                ort.unterpunkt ?? '',
              ) && (
                <Anzeigewaehrungswahl
                  angebot={waehrungsangebot}
                  gewaehlt={anzeigewaehrung}
                  aufWahl={setAnzeigewaehrung}
                />
              )}

            {/*
              **Und nicht über einer Bilanz mit einer einzigen Periodenspalte**
              (R293, R528): Die Verkehrszahl braucht eine Vorspalte, und die gibt
              es dort nicht. Ein Feld, das dasteht und nichts tut, ist schlimmer
              als ein fehlendes. YTD einer einzelnen GuV-Spalte dagegen wirkt —
              es kumuliert seit Geschäftsjahresanfang, nicht gegen eine Vorspalte.
            */}
            {ort.bereich === 'berichte' &&
              spaltenmodus === 'perioden' &&
              ['bilanz', 'guv', 'kapitalflussrechnung'].includes(ort.unterpunkt ?? '') &&
              !(zeitwertfamilie === 'bestand' && periodenbericht?.spalten.length === 1) && (
                <Zeitwertwahl
                  familie={zeitwertfamilie}
                  raster={raster}
                  gewaehlt={zeitwert}
                  aufWahl={zeitwertfamilie === 'bestand' ? setBilanzwert : setStromwert}
                />
              )}

            {/*
              **Hier stand bis zum 2026-09-13 der Zustandsanzeiger** —
              „Arbeitsstand" oder der Abschlussstatus, am rechten Ende der
              Zeile. Er ist zurückgenommen (2.12 R105): Der Freigabezustand
              gehört in den Abschlussprozess, wo er eine Handlung auslöst.
              Über einem Bericht löst er keine aus — er beantwortet keine
              Frage, die beim Zahlenlesen entsteht, und belegt den Platz am
              Zeilenende, den die Auswahl der Kontextzeile braucht.
            */}
          </header>
        )}

        <main className="arbeitsfeld">
          {/* Eine Seite, die älter ist als der Server, sagt es zuerst (7.2 Abschnitt 46, R1441). */}
          <Fassungshinweis />

          {/*
            **Kamen die Rechte nicht, steht es hier** (7.2 Abschnitt 39,
            R1210): Bis dahin stehen keine schreibenden Knöpfe da, und ohne
            diesen Satz sähe die Anwendung aus wie eine reine Leseseite.
          */}
          {rechtelage.ausgefallen && rechtelage.erneut && (
            <Abrufausfall abruf={{ erneut: rechtelage.erneut }} satz={t('rechte.ausgefallen')} />
          )}
          {/*
            **Hier stehen nur noch fachliche Befunde** (7.2 Abschnitt 30,
            R1004). Eine Störung — der Dienst antwortet nicht oder bricht ab —
            steht im Störungsfenster, das um die ganze Anwendung liegt (R1003).
          */}
          {fehler && (
            <div className="meldung">
              <h2>{t('meldung.rechenabbruch.titel')}</h2>
              <p>{satz(fehler.meldung)}</p>
              {/* Der Weg mit Verweis an den Ort (7.1 Abschnitt 13, R1581). */}
              {fehler.handreichung && (
                <p className="handreichung">
                  <Abbruchweg handreichung={fehler.handreichung} kontext={{ kreis, ledger }} />
                </p>
              )}
            </div>
          )}

          {/*
            **Die Kopfzeile nennt die Welt** (2.13 Abschnitt 14, R1114). Wo der
            Bericht eine Reihe der Spaltenansicht hat, steht die Welt selbst
            dort, rechts neben den Knöpfen (R1313) — hier bleiben nur die Sätze,
            die etwas verlangen. Die Spiegel haben keine solche Reihe.
          */}
          {ort.bereich === 'berichte' &&
            (weltabruf.ausgefallen ? (
              <Abrufausfall abruf={weltabruf} />
            ) : (
              <Weltzeile
                geliefert={Boolean(gelieferterBezug)}
                welt={weltabruf.wert}
                kreis={kreis ?? ''}
                ledger={ledger ?? ''}
                kopf={!['bilanz', 'guv', 'kapitalflussrechnung'].includes(ort.unterpunkt ?? '')}
              />
            ))}

          {/*
            **Über dem Bericht steht kein Satz zur Umrechnung** (2.12 Abschnitt
            3.21, R1598). Bis zum 2026-10-12 standen hier Sätze wie „Für A.I.
            Gezeichnetes Kapital ist kein historischer Kurs gepflegt“; was an
            Kursen fehlt, meldet die Arbeitsliste.
          */}

          {/*
            **Die Startseite ist das Kachelbrett, und die Arbeitsliste ist
            seine erste Kachel** (7.5, R337). R140 bleibt wörtlich erfüllt: Wer
            sich anmeldet, sieht seinen Arbeitsvorrat — er sieht ihn nur nicht
            mehr allein. Die Arbeitsliste wird dem Brett fertig übergeben und
            nicht von ihm nachgebaut; **zwei Stellen, die dieselbe Liste
            zusammenstellen, gehen irgendwann auseinander.**
          */}
          {ort.bereich === 'arbeitsliste' && (
            <Kachelbrett
              /*
                **Der Rollenwechsel baut das Brett neu auf** — `key` ist hier
                die Absicht und kein Kniff: Vorlage und Katalog hängen an der
                Rolle, und ein Bauteil, das seine Vorlage einmal beim ersten
                Rendern gelesen hat, zeigte sonst das Brett von vorhin.
              */
              key={rolle}
              rolle={rolle}
              erste={
                /*
                  **Welche erste Kachel, sagt die Rolle** (R353). Der Prüfer
                  hat keinen Arbeitsvorrat (7.1 Abschnitt 6) — ihm die
                  Arbeitsliste hinzustellen hieße, ihm dauerhaft einen leeren
                  Kasten zu zeigen.
                */
                ersteKachel(rolle) === 'abschluesse' ? (
                  <>
                    {/* Serie und Bestzeit stehen über der ersten Kachel,
                        welche es auch ist (7.7, R923). */}
                    <Seriezeile
                      kreis={kreis}
                      ledger={ledger}
                      abschlussstatus={urteil?.abschlussstatus ?? null}
                    />
                    {/* Kamen die Abschlüsse nicht, steht das da und nicht „keiner" (R1210). */}
                    {abschlussabruf.ausgefallen ? (
                      <Abrufausfall abruf={abschlussabruf} />
                    ) : (
                      <Abschlusstafel
                        abschluesse={abschlussabruf.wert}
                        aufSprung={(ziel) => gehe(ziel.bereich, ziel.unterpunkt)}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Der Erfolgsmoment steht über der Liste und nicht in ihr
                        (7.7, R921): Die Liste bleibt, wie sie jeder sieht. */}
                    <Erfolgsmoment
                      kreis={kreis}
                      kreisname={kreisname}
                      ledger={ledger}
                      periode={periode}
                      abschlussstatus={urteil?.abschlussstatus ?? null}
                    />
                    <Seriezeile
                      kreis={kreis}
                      ledger={ledger}
                      abschlussstatus={urteil?.abschlussstatus ?? null}
                    />
                    {/*
                      **Der Einstieg ist das Übersichtsblatt** (7.1 Abschnitt
                      12, R1519); darunter, was die ganze Anwendung betrifft.
                    */}
                    <section className="arbeitsliste">
                      <h1>{bereichsname(findeBereich('arbeitsliste'))}</h1>
                      {ueberwachungAusgefallen && !ueberwachung && (
                        <p className="hinweis">{t('ueberwachung.ausgefallen')}</p>
                      )}
                      <Ueberwachungsblatt
                        stand={ueberwachung}
                        auswahl={auswahl}
                        aufSprung={blattsprung}
                        aufGeaendert={setUeberwachung}
                      />
                      <Vorrat zeilen={zeilen} aufSprung={gehe} />
                    </section>
                  </>
                )
              }
              quellen={{ lauf, urteil, bilanz, guv, pruefung, messung, vorgaenge, datenquellen }}
              kontext={{
                kreis,
                ledger,
                periode,
                raster,
                // **Die Herkunftszeile nennt Namen** (R705) — sie setzt ihren
                // Text selbst zusammen und fragt die gemeinsame Stelle nicht.
                namen: Object.fromEntries([
                  ...(auswahl?.kreise ?? []).map((e) => [e.schluessel, kreisnameImKatalog(e.schluessel)]),
                  ...(auswahl?.ledger ?? []).map((e) => [e.schluessel, bewertungsebenenname(e.schluessel)]),
                ]),
              }}
              auswahl={auswahl}
              laufperioden={laufperioden}
              aufSprung={(ziel) => gehe(ziel.bereich, ziel.unterpunkt)}
              aufNachweis={(bezug, raum) => {
                // **Jeder Operand führt in den Wertenachweis** (R342, R213) —
                // in denselben, den ein Klick im Bericht öffnet, und mit der
                // Herkunft der Kachel statt der Kontextzeile: Eine
                // festgezurrte Kachel weist ihre eigene Periode nach.
                if (!bezug.nachweis) {
                  return
                }

                setNachweis({
                  kreis: raum.kreis,
                  ledger: raum.ledger,
                  art: bezug.nachweis.art,
                  periode: raum.periode,
                  position: bezug.nachweis.position,
                  positionsname: bezug.name,
                  konto: null,
                  einheit: null,
                  spalte: 'konzern',
                })
              }}
            />
          )}

          {ort.bereich === 'abschluss' && ort.unterpunkt === 'monitor' && (
            <Konzernabschlussmonitor
              lauf={lauf}
              urteil={urteil}
              gibtFrei={gibtFrei}
              aufFreigabe={freigeben}
              kontext={meldungskontext}
              aufSprung={gehe}
              aufGeaendert={() => {
                if (lauf) {
                  void ladeLauf(lauf)
                }
              }}
            />
          )}

          {/* Das Regelwerk, nach dem gerechnet wird (2.11 Abschnitt 9, R779).
              Es braucht weder Kreis noch Periode: Eine Ausgabe gilt fuer das
              ganze Haus und ab einer Periode, nicht in einer. */}
          {ort.bereich === 'abschluss' && ort.unterpunkt === 'konzernhandbuch' && <Konzernhandbuch />}

          {/*
            Der Konzernabschluss (2.13 Abschnitt 8, R787): Er haengt an einem
            Kreis und umfasst ein Geschaeftsjahr — Kreis und Periode der
            Kontextzeile sind hier nicht seine, deshalb steht er ohne sie.
          */}
          {ort.bereich === 'abschluss' && ort.unterpunkt === 'konzernabschluesse' && (
            <Konzernabschluesse />
          )}


          {/*
            **Eine Liste, die des Programms** (2.9 Abschnitt 10.6, R837).
            Bis zum 2026-09-29 standen hier zwei: oben die Folge des
            Anwenders, darunter die Vorgänge des Laufs — dieselben Schritte in
            anderer Reihenfolge. Jetzt trägt die eine Liste die Zahl der
            Buchungen aus dem Lauf der Kontextzeile mit.
          */}
          {ort.bereich === 'abschluss' && ort.unterpunkt === 'schritte' && (
            <Konsolidierungsschritte />
          )}

          {ort.bereich === 'berichte' && spiegelart[ort.unterpunkt ?? ''] && (
            <section>
              {/*
                **Keine Überschrift über dem Spiegel** (R475, wie R411 in Bilanz
                und GuV): Sie steht in der Tabelle, im Feld links oben, das die
                Blockzeile dort frei lässt. Eine Zeile Bildschirm gespart.
              */}
              {/* Ein Spiegel auf „Nicht prüfen" sagt es selbst (R913). */}
              <Ungeprueftvermerk spiegel={spiegelart[ort.unterpunkt ?? ''] ?? ''} periode={periode} />
              {spiegel ? (
                <Spiegel
                  spiegel={spiegel}
                  aufriss={aufriss}
                  quelle={spiegelquelle}
                  aufNachweis={nachweisruf}
                  aufEigenschaften={setEigenschaften}
                />
              ) : (
                <p className="hinweis">{t('kontext.rechnet')}</p>
              )}
            </section>
          )}

          {/*
            Die Kapitalflussrechnung (2.8, G3).

            **Dieselbe Bedienung wie Bilanz und GuV** — Kreis, Bewertungsebene
            und Periode stehen oben in der Kontextzeile, der Zeitraum kommt aus
            derselben Knopfreihe, und der Aufriss ist derselbe Pfad auf der
            Zeilenachse (R171). Was hinzukommt, ist die Schaltfläche für die
            Stichtagskursanpassung — sie steht seit dem 2026-09-13 in der
            Berichtsleiste neben der Spaltenwahl (R479), weil beide dasselbe
            entscheiden: wie derselbe Lauf gelesen wird.
          */}
          {ort.bereich === 'berichte' && ort.unterpunkt === 'kapitalflussrechnung' && (
            <section>
              {/*
                **Keine Überschrift über dem Bericht** (R475, wie R411): Sie
                steht in der Tabelle, in einer übergreifenden Titelzeile über
                allen Spalten — mitsamt der Spezifikation (R476).
              */}

              {/*
                **Dieselbe Spaltenwahl wie in Bilanz und GuV** (R167) — drei
                Bilder, dieselben Zeilen, drei Fragen an dieselben Zahlen. Das
                Raster erscheint hier auch außerhalb des Periodenbildes: Bei
                einer Stromgröße sagt es, über welchen Zeitraum gerechnet wird,
                und das gilt in jedem Bild.
              */}
              <div className="berichtsleiste">
                <Spaltenwahl modus={spaltenmodus} aufModus={setSpaltenmodus} nur={bildwahl} geliefert={gelieferterBezug !== undefined} />
                {spaltenmodus === 'partner' && kapitalfluss && (
                  <Gesellschaftswahl
                    einheiten={einheitenIn(kapitalfluss)}
                    gewaehlt={gesellschaft}
                    aufWahl={setGesellschaft}
                  />
                )}

                {/* Die Welt, rechts neben den Knöpfen (R1313). */}
                {!weltabruf.ausgefallen && <Weltkopf geliefert={Boolean(gelieferterBezug)} welt={weltabruf.wert} />}

                {/*
                  **Ein einzelnes Bedienelement bekommt keine eigene Leiste**
                  (R479). Der Umschalter für den gesonderten Ausweis der
                  Stichtagskursanpassung (R251) stand bis zum 2026-09-13 in
                  einer eigenen Zeile über der Rechnung; nachdem die drei
                  Angaben daneben ihren Platz gewechselt haben, wäre das eine
                  Bildschirmzeile für einen Knopf gewesen.
                */}
                <Kursanpassungsschalter
                  kursanpassung={kursanpassung}
                  aufKursanpassung={setKursanpassung}
                />
              </div>

              {(() => {
                if (spaltenmodus === 'perioden') {
                  return kfrPerioden ? (
                    <KapitalflussNachPerioden
                      bericht={kfrPerioden}
                      kumuliert={kumuliert}
                      // **Die GuV unter Zeile 1, je Spalte** (2.8 Abschnitt
                      // 13.33, R1105): derselbe Zeitraum, dasselbe Raster,
                      // dieselbe Sicht wie die Rechnung darüber.
                      guvLaden={() =>
                        gelieferterBezug
                          ? api.gelieferteperioden(gelieferterBezug, ledger, 'guv', raster, periode, von || undefined, kumuliert, plan || null)
                          : api.periodenbericht(kreis, ledger, 'guv', raster, periode, von || undefined, kumuliert)
                      }
                      guvKennung={[gelieferterBezug ?? kreis, ledger, raster, periode, von, kumuliert, plan, kfrStand].join('|')}
                      aufriss={aufriss}
                      quelle={kfrPeriodenquelle}
                      aufNachweis={kfrNachweisruf}
                      aufAnlegen={darf('kfrZeileAnlegen') ? rufeKfrAnlage : undefined}
                      aufAenderung={() => setKfrStand((n) => n + 1)}
                    />
                  ) : (
                    <p className="hinweis">{t('kontext.rechnet')}</p>
                  )
                }

                if (!kapitalfluss) {
                  return <p className="hinweis">{t('kontext.rechnet')}</p>
                }

                return (
                  <>
                    {spaltenmodus === 'konsolidierung' ? (
                      <Kapitalflussrechnung
                        rechnung={kapitalfluss}
                        plan={plan || null}
                        aufriss={aufriss}
                        quelle={kfrquelle}
                        aufAenderung={() => setKfrStand((n) => n + 1)}
                        aufNachweis={kfrNachweisruf}
                        aufAnlegen={darf('kfrZeileAnlegen') ? rufeKfrAnlage : undefined}
                      />
                    ) : (
                      <KapitalflussNachStruktur
                        rechnung={kapitalfluss}
                        plan={plan || null}
                        bild={spaltenmodus}
                        gesellschaft={spaltenmodus === 'partner' ? gesellschaft || null : null}
                        partnerzellen={kfrPartnerwuerfel}
                        aufriss={aufriss}
                        quelle={kfrquelle}
                        aufAenderung={() => setKfrStand((n) => n + 1)}
                        aufNachweis={kfrNachweisruf}
                        aufAnlegen={darf('kfrZeileAnlegen') ? rufeKfrAnlage : undefined}
                      />
                    )}
                  </>
                )
              })()}
            </section>
          )}

          {ort.bereich === 'berichte' && (ort.unterpunkt === 'bilanz' || ort.unterpunkt === 'guv') && (
            <section>
              {/*
                **Keine Überschrift über dem Bericht** (R411): Sie steht in der
                Tabelle, in der Spalte, die vorher „Position" hieß. Eine Zeile
                Bildschirm gespart, und der Anwender liest den Namen dort, wo er
                ohnehin hinsieht.
              */}

              {/*
                Die Spaltenwahl steht über dem Bericht und nicht darin: Sie
                gilt für Bilanz, GuV und später die Kapitalflussrechnung
                gleichermaßen, und ein Anwender, der sie in der Bilanz gelernt
                hat, soll sie in der GuV wiederfinden.
              */}
              <div className="berichtsleiste">
                {/*
                  **Die Gliederung steht neben der Spaltenwahl, nicht in der
                  Kontextzeile.** Beide entscheiden, wie derselbe Lauf gelesen
                  wird — Kreis, Ledger und Periode entscheiden dagegen, WELCHER
                  Lauf es ist.

                  **Und nur, wo es etwas zu wählen gibt** (R293, in der
                  Kontextzeile fortgeschrieben als R330): Ein Auswahlfeld mit
                  einem einzigen Eintrag steuert nichts und behauptet trotzdem,
                  der Bildschirm zeige etwas für diese Auswahl.

                  *Berichtigt am 2026-09-13: Hier stand R146. Der sagt
                  „erweiterbar in der Breite, nicht in der Tiefe" und meint den
                  Vorrat des Produkts — nicht, wo ein Bedienelement steht.*
                */}
                {planwahl.length > 1 && (
                  <div className="feld gliederungswahl">
                    <label htmlFor="gliederung">{t('gliederung.wahl')}</label>
                    <Auswahlfeld
                      id="gliederung"
                      wert={
                        plan ||
                        (planwahl.find((p) => p.istVorgabe) ?? planwahl[0])?.schluessel ||
                        ''
                      }
                      eintraege={gliederungswahl}
                      aufWahl={(gewaehlt) => {
                        setPlan(gewaehlt)
                        if (lauf) {
                          void ladeLauf(lauf, gewaehlt)
                        }
                      }}
                    />
                  </div>
                )}

                <Spaltenwahl modus={spaltenmodus} aufModus={setSpaltenmodus} nur={bildwahl} geliefert={gelieferterBezug !== undefined} />

                {/*
                  Im Partnerbild die Gesellschaft: „alle" ist der Kreis mit
                  Summe, Konsolidierung und Konzern; eine gewählte zeigt nur
                  ihre Handelsbilanz II je Partner, mit ihrem Namen über der
                  Summenspalte.
                */}
                {spaltenmodus === 'partner' && berichtssatz && (
                  <Gesellschaftswahl
                    einheiten={einheitenIm(berichtssatz)}
                    gewaehlt={gesellschaft}
                    aufWahl={setGesellschaft}
                  />
                )}

                {/* Die Welt, rechts neben den Knöpfen (R1313). */}
                {!weltabruf.ausgefallen && <Weltkopf geliefert={Boolean(gelieferterBezug)} welt={weltabruf.wert} />}

                {/*
                  Die Suche steht dort, wo die Frage entsteht: neben dem
                  Bericht, den sie durchsucht. Ein kleines Symbol, das
                  aufklappt — und Strg+F für die, die es ohnehin drücken.
                */}
                <Suchfeld
                  offen={sucheOffen}
                  eingabe={suchtext}
                  funde={funde}
                  index={suchstelle}
                  aufOeffnen={(auf) => {
                    setSucheOffen(auf)
                    if (!auf) {
                      setSuchtext('')
                    }
                  }}
                  aufEingabe={setSuchtext}
                  aufSprung={(schritt) =>
                    funde.length > 0 &&
                    springeZu((suchstelle + schritt + funde.length) % funde.length)
                  }
                />
              </div>

              {/*
                **Wer im Monat nichts geliefert hat, steht über dem Bericht**
                (2.12 Abschnitt 3.20, R975, R978) — in allen Bildern zum
                Stichtag. Das Periodenbild sagt es im Spaltenkopf.
              */}
              {spaltenmodus !== 'perioden' && (
                <Lieferstand
                  hinweise={(ort.unterpunkt === 'bilanz' ? bilanz : guv)?.lieferstand}
                  periode={periode}
                />
              )}

              {(() => {
                /*
                  **Der Pilot war ein Schalter, kein Teilbau** (R500) — und
                  das Ausrollen ist deshalb das Entfernen einer Bedingung:
                  Bilanz **und GuV** tragen die fünfte Menügruppe, in allen
                  vier Spaltenbildern (2026-09-13, nach der Abnahme).

                  Beide lesen denselben Positionsplan, beide bestehen aus
                  Positionen mit Konten darunter — für das Bauteil ist der
                  Unterschied zwischen ihnen die Positionsart, und die kommt
                  ohnehin vom Elter (R321).
                */
                /*
                  **Steht kein Lauf, steht die Gliederung** (2.12 Abschnitt
                  3.11, R717): Bilanz und GuV zeigen ihre Positionen mit den
                  Konten darunter, die Wertspalten bleiben leer. Erst wenn
                  auch die Gliederung fehlt, ist nichts zu zeigen.
                */
                // Kam der Bericht nicht, steht das am Ort — weder „Keine
                // Daten" noch die leere Gliederung (R1210).
                if (berichtausfall) {
                  return <Abrufausfall abruf={{ erneut: () => setBerichtsversuch((n) => n + 1) }} />
                }

                const gerechnet = ort.unterpunkt === 'bilanz' ? bilanz : guv
                const satz =
                  gerechnet ??
                  (gliederungsbild
                    ? ausGliederung(gliederungsbild, berichtsart, ledger)
                    : null)

                if (!satz) {
                  // „Keine Daten" nur, wenn feststeht, dass es keine gibt (R1210).
                  if (gliederungsabruf.ausgefallen) {
                    return <Abrufausfall abruf={gliederungsabruf} />
                  }
                  if (gliederungsabruf.laedt) {
                    return null
                  }
                  return <KeineDaten konzern={!gelieferterBezug} />
                }

                if (spaltenmodus === 'struktur' || spaltenmodus === 'kurz' || spaltenmodus === 'partner') {
                  return (
                    <BerichtNachStruktur
                      offeneStufen={offeneStufen}
                      aufStufe={stufeUmschalten}
                      bericht={satz}
                      bild={spaltenmodus}
                      /*
                        **Im Partnerbild die gewählte Gesellschaft, im
                        Strukturbild die des Berichtsbezugs** (R714): Wer oben
                        eine Gesellschaft gewählt hat, sieht ihre
                        Handelsbilanz II und nicht die des ganzen Kreises.
                      */
                      gesellschaft={
                        spaltenmodus === 'partner'
                          ? gesellschaft || null
                          : bezugsgesellschaft
                      }
                      partnerzellen={partnerwuerfel}
                      aufriss={aufriss}
                      quelle={berichtsquelle}
                      treffer={treffer}
                      aufNachweis={nachweisruf}
                      aufEigenschaften={setEigenschaften}
                      aufReihenfolge={darf('reihenfolgeSetzen') ? setReihenfolge : undefined}
                      aufBuchen={lauf && darf('buchen') ? rufeBuchung : undefined}
                      aufAnlegen={darf('kontoAnlegen') && darf('unterpositionAnlegen') ? rufeAnlage : undefined}
                    />
                  )
                }

                if (spaltenmodus === 'perioden') {
                  return periodenbericht ? (
                    <BerichtNachPerioden
                      bericht={periodenbericht}
                      verkehrszahl={verkehrszahl}
                      kumuliert={kumuliert}
                      aufriss={aufriss}
                      quelle={periodenquelle}
                      treffer={treffer}
                      aufNachweis={nachweisruf}
                      aufEigenschaften={setEigenschaften}
                      aufReihenfolge={darf('reihenfolgeSetzen') ? setReihenfolge : undefined}
                      aufBuchen={lauf && darf('buchen') ? rufeBuchung : undefined}
                      aufAnlegen={darf('kontoAnlegen') && darf('unterpositionAnlegen') ? rufeAnlage : undefined}
                    />
                  ) : (
                    <p className="hinweis">{t('kontext.rechnet')}</p>
                  )
                }

                /*
                  **Unter der Bilanz stand bis zum 2026-09-13 ein Statussatz**
                  — „Aktiva und Passiva stimmen überein." Er ist ersatzlos weg
                  (R488).

                  Der Satz beantwortete keine Frage, die offen wäre: Die
                  Bilanzgleichung ist **Grundbedingung**, nicht Befund. Kein
                  Beleg kommt unausgeglichen in das Journal (R195, Zwang in der
                  Datenbank), keine unvollständige Lieferung wird übernommen
                  (R488), und eine Handbuchung trägt Soll an Haben (R366). Eine
                  Bilanz, die nicht aufgeht, kann es damit nicht geben — und ein
                  Satz, der eine Selbstverständlichkeit bestätigt, kostet
                  dauerhaft eine Zeile Bildschirm (7.3 Abschnitt 12).

                  **Der Gegenfall steht weiterhin da, wo er hingehört:** in der
                  Arbeitsliste und als Freigabebefund J7 — dort, wo jemand
                  handeln müsste, nicht unter einer Zahl, die stimmt.
                */
                return (
                  <Bericht
                    bericht={satz}
                    aufriss={aufriss}
                    quelle={berichtsquelle}
                    treffer={treffer}
                    aufNachweis={nachweisruf}
                    aufEigenschaften={setEigenschaften}
                    aufReihenfolge={darf('reihenfolgeSetzen') ? setReihenfolge : undefined}
                    aufBuchen={lauf && darf('buchen') ? rufeBuchung : undefined}
                    aufAnlegen={darf('kontoAnlegen') && darf('unterpositionAnlegen') ? rufeAnlage : undefined}
                    schichtenOffen={schichtenOffen}
                    aufSchichten={schichtenUmschalten}
                  />
                )
              })()}
            </section>
          )}

          {ort.bereich === 'nachweis' && ort.unterpunkt === 'buchungsjournal' && (
            <section>
              <h1>{t('bereich.nachweis.buchungsjournal')}</h1>

              {/*
                **Zwei Sichten auf dieselben Zeilen** (2.14 Abschnitt 7, 2.16
                Abschnitt 5): der Belegordner, in dem der Anwender arbeitet und
                aufräumt — und die chronologische Liste, in die der Link aus
                dem Wertenachweis führt. Kommt er von dort, steht sie vorne;
                sonst der Ordner.
              */}
              <div className="journalleiste">
                <div className="sichtwahl" role="tablist">
                  {(['ordner', 'suche', 'chronologisch'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="tab"
                      aria-selected={journalsicht === s}
                      className={journalsicht === s ? 'gewaehlt' : undefined}
                      onClick={() => setJournalsicht(s)}
                    >
                      {t(`journal.sicht.${s}` as Sprachschluessel)}
                    </button>
                  ))}
                </div>

                {/*
                  **Die zwei Filter, die das Journal wirklich hat** (2.14
                  Abschnitt 7). Sie standen bis zum 2026-09-12 oben in der
                  Kontextzeile — zusammen mit Konsolidierungskreis, Raster und
                  Arbeitsstand, die hier nichts steuerten. Die Leiste ist
                  deshalb fort (R330), und was wirkt, steht dort, wo es wirkt.

                  Eine Auswahlliste statt des Zeitschiebers: Der Schieber ist
                  für einen Zeit*raum* gebaut und für die Bewegung darin; ein
                  Beleg gehört zu **einer** Periode, und mehr als sie zu wählen
                  gibt es hier nicht.
                */}
                {/*
                  **Die Gesellschaft steht in derselben Zeile** (2026-09-12).

                  Sie hatte darunter eine eigene Leiste, und die kostete eine
                  ganze Bildschirmzeile für ein einziges Auswahlfeld. Es sind
                  drei Filter derselben Art — womit bewertet, welcher Monat,
                  welche Gesellschaft —, und drei Filter gehören
                  nebeneinander, nicht untereinander.
                */}
                {journalsicht !== 'chronologisch' && (
                  <div className="feld">
                    <label htmlFor="journaleinheit">{t('journal.gesellschaft')}</label>
                    <Auswahlfeld
                      id="journaleinheit"
                      wert={journaleinheit}
                      eintraege={journalwahl}
                      aufWahl={setJournaleinheit}
                    />
                  </div>
                )}

                <div className="feld">
                  <label htmlFor="journalledger">{t('kontext.ledger')}</label>
                  <Auswahlfeld
                    id="journalledger"
                    wert={ledger}
                    eintraege={ledgerwahl}
                    aufWahl={setLedger}
                  />
                </div>

                <div className="feld">
                  <label htmlFor="journalperiode">{t('journal.periode')}</label>
                  <select
                    id="journalperiode"
                    value={journalperiode}
                    onChange={(e) => setJournalperiode(Number(e.target.value))}
                  >
                    <option value={0}>{t('journal.periode.alle')}</option>
                    {journalperioden.map((p) => (
                      <option key={p} value={p}>
                        {periodenname(p)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/*
                **Ordner und Suche sind dasselbe Brett** (2026-09-12).

                Die in 2.14 Abschnitt 7 vorgesehene Zeilen-Sicht (H10) bleibt,
                was sie war — flach, über alle Ordner hinweg —, aber sie tritt
                nicht mehr an die Stelle des Ordnerbaums. Der Baum steht in
                beiden Sichten links und zeigt beim Suchen, wie viele Treffer
                wo liegen; die Treffer stehen rechts, wo sonst die Belege des
                Ordners stehen. Der Ordner bleibt damit ein *Ort* statt einer
                Spalte — und der Sprung auf einen Treffer (R209) ist kein
                Sichtwechsel mehr, sondern ein Aufschlagen an Ort und Stelle.
              */}
              {/* Der Rückweg, wenn aus den Datenimporten gesprungen wurde (R1246). */}
              {journalrueckweg && (
                <p>
                  <button
                    type="button"
                    className="ortsverweis"
                    onClick={() => {
                      window.location.hash = rueckweg(journalrueckweg)
                    }}
                  >
                    {t('journal.zurueck.datenimporte')}
                  </button>
                </p>
              )}

              {(journalsicht === 'ordner' || journalsicht === 'suche') && (
                <Belegordner
                  ledger={ledger}
                  periode={periode}
                  filterperiode={journalperiode || null}
                  kreis={kreis}
                  einheiten={journaleinheiten}
                  einheit={journaleinheit}
                  aufEinheiten={setJournaleinheiten}
                  aufEinheit={setJournaleinheit}
                  ziel={journalziel}
                  aufZielErreicht={journalzielErreicht}
                  sicht={journalsicht === 'suche' ? 'suche' : 'belege'}
                />
              )}

              {journalsicht === 'chronologisch' && (
              <Buchungsjournal
                lauf={lauf}
                ledger={ledger}
                periode={journalperiode}
                ziel={{
                  lauf: ort.ziel.lauf ? Number(ort.ziel.lauf) : undefined,
                  vorgang: ort.ziel.vorgang ? Number(ort.ziel.vorgang) : undefined,
                  beleg: ort.ziel.beleg ? Number(ort.ziel.beleg) : undefined,
                  konto: ort.ziel.konto,
                  einheit: ort.ziel.einheit,
                }}
                aufZielGeloest={() => gehe('nachweis', 'buchungsjournal')}
              />
              )}
            </section>
          )}

          {ort.bereich === 'nachweis' && ort.unterpunkt === 'aenderungsnachweis' && (
            <section>
              <h1>{t('bereich.nachweis.aenderungsnachweis')}</h1>
              {pruefung ? (
                <>
                  <div className="kennzahlen">
                    <Kennzahl name={t('nachweis.eintraege')}>{pruefung.eintraege}</Kennzahl>
                    <Kennzahl name={t('nachweis.kette')}>
                      {t(pruefung.unversehrt ? 'nachweis.unversehrt' : 'nachweis.unterbrochen')}
                    </Kennzahl>
                    {pruefung.ersterBruchBei !== null && (
                      <Kennzahl name={t('nachweis.bruch')}>{pruefung.ersterBruchBei}</Kennzahl>
                    )}
                  </div>
                  <p className="erlaeuterung">{satz(pruefung.meldung)}</p>
                </>
              ) : (
                <KeineDaten konzern={!gelieferterBezug} />
              )}
            </section>
          )}

          {/*
            **Die Prüferansicht** (2.13 Abschnitt 9): der Punkt stand seit dem
            ersten Aufbau der Navigation im Menü und war leer. Er beantwortet
            die Frage, die ein Prüfer als erste stellt — nach welchen Regeln
            rechnet dieses Programm, und halten sie.
          */}
          {ort.bereich === 'nachweis' && ort.unterpunkt === 'prueferansicht' && (
            <section>
              <h1>{t('bereich.nachweis.prueferansicht')}</h1>
              <Prueferansicht />
            </section>
          )}

          {/* Was bei Freigaben übergangen wurde oder ungeprüft blieb (R912, R913). */}
          {ort.bereich === 'nachweis' && ort.unterpunkt === 'uebergangene' && <Uebergangene />}

          {/*
            Der Systemzustand: die zwei Zusagen, die bis zum 2026-09-11 in der
            Fußzeile standen. Wer eine Bilanz liest, braucht sie nicht; wer die
            Anlage betreibt, sucht sie hier. Je Zusage eine Ampel und der Satz,
            der vorher unten stand — die Einzelheiten haben ihre eigenen
            Bildschirme (Antwortzeiten hier im Betrieb, der Änderungsnachweis
            im Nachweis).
          */}
          {ort.bereich === 'betrieb' && ort.unterpunkt === 'systemzustand' && (
            <section>
              <h1>{t('bereich.betrieb.systemzustand')}</h1>
              {pruefung || messung || datenquellen ? (
                <div className="zusagenliste">
                  {/*
                    Die Datenquellen mit offenen Zielfeldern (R523): gelb, kein
                    Blocker. Eine neue Dimension steht an jeder Quelle als offen,
                    bis jemand sie zuordnet oder als „nicht geliefert" abhakt —
                    und hier steht, an wie vielen.
                  */}
                  {datenquellen && (
                    <div className="zusage">
                      <h2>
                        <span
                          className={`ampel ${datenquellen.some((q) => q.istAktiv && (q.stand?.offen ?? 0) > 0) ? 'warn' : 'gut'}`}
                        />
                        {t('bereich.datenimport.quellen')}
                      </h2>
                      {(() => {
                        const aktive = datenquellen.filter((q) => q.istAktiv)
                        const offene = aktive.filter((q) => (q.stand?.offen ?? 0) > 0)
                        return offene.length === 0 ? (
                          <p>{t('zustand.quellen.vollstaendig', { anzahl: anzahlwort(aktive.length) })}</p>
                        ) : (
                          <>
                            <p>{t('zustand.quellen.offen', { anzahl: anzahlwort(offene.length), gesamt: anzahlwort(aktive.length) })}</p>
                            <ul className="quellenwarnung">
                              {offene.map((q) => (
                                <li key={q.schluessel}>
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      gehe('datenimport', 'quellen')
                                    }}
                                  >
                                    {mitCode(q.code, beschriftung(q.namen), ' · ', q.schluessel)}
                                  </a>{' '}
                                  — {t('quelle.liste.offen', { anzahl: anzahlwort(q.stand?.offen ?? 0) })}
                                </li>
                              ))}
                            </ul>
                          </>
                        )
                      })()}
                    </div>
                  )}
                  {pruefung && (
                    <div className="zusage">
                      <h2>
                        <span className={`ampel ${pruefung.unversehrt ? 'gut' : 'schlecht'}`} />
                        {t('bereich.nachweis.aenderungsnachweis')}
                      </h2>
                      <p>{t('zustand.nachweis', { meldung: satz(pruefung.meldung) })}</p>
                      <div className="kennzahlen">
                        <Kennzahl name={t('nachweis.eintraege')}>{pruefung.eintraege}</Kennzahl>
                        <Kennzahl name={t('nachweis.kette')}>
                          {t(pruefung.unversehrt ? 'nachweis.unversehrt' : 'nachweis.unterbrochen')}
                        </Kennzahl>
                      </div>
                    </div>
                  )}
                  {messung && (
                    <div className="zusage">
                      <h2>
                        <span className={`ampel ${messung.p95Ms <= messung.budgetMs ? 'gut' : 'schlecht'}`} />
                        {t('bereich.betrieb.antwortzeiten')}
                      </h2>
                      <p>
                        {t('zustand.antwortzeit', {
                          anzahl: anzahlwort(messung.anzahl),
                          mittel: anzahlwort(messung.mittlereMs),
                          p95: anzahlwort(messung.p95Ms),
                          budget: anzahlwort(messung.budgetMs),
                        })}
                      </p>
                      <p className="erlaeuterung">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            gehe('betrieb', 'antwortzeiten')
                          }}
                        >
                          {t('zustand.einzelheiten')}
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <KeineDaten konzern={!gelieferterBezug} />
              )}
            </section>
          )}

          {ort.bereich === 'betrieb' && ort.unterpunkt === 'antwortzeiten' && (
            <section>
              <h1>{t('bereich.betrieb.antwortzeiten')}</h1>
              {messung ? (
                <>
                  <div className="kennzahlen">
                    <Kennzahl name={t('messung.aufrufe')}>{anzahlwort(messung.anzahl)}</Kennzahl>
                    <Kennzahl name={t('messung.schnellste')}>{messung.schnellsteMs} ms</Kennzahl>
                    <Kennzahl name={t('messung.mittel')}>{messung.mittlereMs} ms</Kennzahl>
                    <Kennzahl name={t('messung.p95')}>{messung.p95Ms} ms</Kennzahl>
                    <Kennzahl name={t('messung.langsamste')}>{messung.langsamsteMs} ms</Kennzahl>
                    <Kennzahl name={t('messung.zusage')}>{messung.budgetMs} ms</Kennzahl>
                  </div>
                  <p className="erlaeuterung">
                    {t('messung.erlaeuterung', {
                      ueber: anzahlwort(messung.ueberBudget),
                      anzahl: anzahlwort(messung.anzahl),
                    })}
                  </p>
                </>
              ) : (
                <KeineDaten konzern={!gelieferterBezug} />
              )}
            </section>
          )}

          {/*
            **Die Platzhalter-Anmeldung** (R351) — sie steht im Bereich
            *Anwender und Rechte*, weil sie dort später wirklich hingehört:
            Kommt die Anmeldung aus Bereich 8, tritt sie an genau diese Stelle,
            und der Anwender sucht nichts an einem neuen Ort.
          */}
          {ort.bereich === 'anwender' && ort.unterpunkt === 'liste' && (
            <Anwenderliste angemeldet={angemeldet} />
          )}

          {ort.bereich === 'betrieb' && ort.unterpunkt === 'sprachen' && (
            <Sprachen
              gewaehlt={sprache}
              zahlenformat={zahlenformat}
              aufWechsel={wechsleSprache}
              aufZahlenformat={wechsleZahlenformat}
            />
          )}

          {/*
            Die Wechselkurse (7.6). Sie stehen unter *Stammdaten* und bekommen
            trotzdem einen eigenen Bildschirm statt des Eigenschaftsblattes:
            Eine Kurstabelle ist eine Tabelle mit Zeitachse und kein Blatt aus
            Eigenschaften — 7.4 hat sie ausdrücklich ausgenommen (Abschnitt 9,
            Punkt 2).
          */}
          {ort.bereich === 'stammdaten' && ort.unterpunkt === 'kurse' && (
            <section>
              <h1>{t('bereich.stammdaten.kurse')}</h1>
              <Wechselkurse />
            </section>
          )}

          {/*
            Die Tabelle der historischen Kurse je Gesellschaft (2.6 Abschnitt
            2.12). Mit Kontextzeile: Was offen ist, hängt am Kreis, an der
            Bewertungsebene und am Monat, die oben stehen.
          */}
          {ort.bereich === 'stammdaten' && ort.unterpunkt === 'historische-kurse' && (
            <section>
              <h1>{t('bereich.stammdaten.historische-kurse')}</h1>
              {/*
                **Der Verweis eines Rechenabbruchs wählt vor** (R1583):
                Gesellschaft und Monat aus der Meldung, Kreis und
                Bewertungsebene aus dem Konzernabschluss, der gerechnet wurde.
              */}
              <HistorischeKurse
                kreis={ort.ziel.kreis || kreis}
                ledger={ort.ziel.ledger || ledger}
                periode={Number(ort.ziel.periode) || periode}
                vorwahl={ort.ziel.einheit}
                aufGeaendert={() => {
                  setHistorischVersuch((n) => n + 1)
                  setBerichtsversuch((n) => n + 1)
                }}
              />
            </section>
          )}

          {/*
            Der Bereich Perioden (2.19). Er bringt seine eigene Überschrift
            mit: Der Bildschirm ist der Bereich, und eine Zeile „Perioden ›
            Zeitachse" über einem einzigen Blatt wäre eine Umständlichkeit
            ohne Ertrag.
          */}
          {ort.bereich === 'perioden' && <Perioden />}

          {ort.bereich === 'datenimport' && ort.unterpunkt === 'importmapping' && (
            <Kontenzuordnung />
          )}

          {/* --- Die Hilfe (R304). Sie springt in die Bildschirme, die sie
              beschreibt: Eine Anleitung, aus der man nicht hinkommt, laesst den
              Anwender den Weg zweimal suchen. --- */}
          {ort.bereich === 'hilfe' && ort.unterpunkt !== 'begleiter' && (
            <Hilfe unterpunkt={ort.unterpunkt} aufSprung={gehe} />
          )}

          {/* --- Der Begleiter (7.1 Abschnitt 10, R545-R549): dieselben
              Anleitungen als Abhakliste fuer das zweite Fenster. Sein Sprung
              geht ins Hauptfenster, siehe `sprungEmpfangen` oben. --- */}
          {ort.bereich === 'hilfe' && ort.unterpunkt === 'begleiter' && <Begleiter aufSprung={gehe} />}

          {/* --- Der Arbeitsbereich Datenimport (5.1, R296-R301). Zwei
              Bildschirme: das Verzeichnis der Quellen und die Lieferung, die
              geprueft wird, bevor sie uebernommen wird. --- */}
          {ort.bereich === 'datenimport' && ort.unterpunkt === 'quellen' && (
            <Datenquellen />
          )}

          {ort.bereich === 'datenimport' && ort.unterpunkt === 'packagevorlagen' && (
            <Packagevorlagen periode={periode} />
          )}

          {ort.bereich === 'datenimport' && ort.unterpunkt === 'lieferungen' && (
            // Der Verweis aus der Warnung im Konzernabschluss-Monitor öffnet die Maske „Aktualisieren" (R880).
            <Lieferung
              aktualisieren={ort.ziel.aktualisieren ? Number(ort.ziel.aktualisieren) : null}
              liste={ort.ziel.liste ?? null}
            />
          )}

          {/* --- Der Konzernanhang (2.15). Ein eigener Bereich, weil eine
              Angabe verfasst, zugeliefert, durchgesehen und freigegeben wird —
              ein Bericht wird nur gelesen (R217). --- */}
          {ort.bereich === 'anhang' && ort.unterpunkt === 'anhangsbuch' && kreis && ledger && periode > 0 && (
            <section>
              <h1>{t('bereich.anhang.anhangsbuch')}</h1>
              <Anhangsbuch
                kreis={kreis}
                ledger={ledger}
                periode={periode}
                sprache={sprache}
                aufNachweis={(ziel) => setNachweis({ kreis, ledger, periode, ...ziel })}
              />
            </section>
          )}

          {ort.bereich === 'anhang' && ort.unterpunkt === 'angabenkatalog' && (
            <section>
              <h1>{t('bereich.anhang.angabenkatalog')}</h1>
              <Angabenkatalog sprache={sprache} />
            </section>
          )}

          {ort.bereich === 'anhang' && ort.unterpunkt === 'vollstaendigkeit' && kreis && ledger && periode > 0 && (
            <section>
              <h1>{t('bereich.anhang.vollstaendigkeit')}</h1>
              <Vollstaendigkeit kreis={kreis} ledger={ledger} periode={periode} />
            </section>
          )}

          {/*
            *Export und Import* (5.1 Abschnitt 10, R560–R567): der einzige
            Punkt unter Stammdaten, der über **alle** Objektarten geht. Er
            steht deshalb vor dem Blatt und nicht darin — STAMMDATENARTEN
            kennt ihn nicht, und genau das ist richtig.
          */}
          {ort.bereich === 'stammdaten' && ort.unterpunkt === 'exportimport' && <Stammdatenaustausch />}

          {/*
            Der Bereich *Stammdaten* — und der Unterpunkt *Kreise* aus der
            Konzernstruktur, der dasselbe Blatt bedient (R156: ein Ort, zwei
            Wege). Welche Objektarten hier hingehören, sagt STAMMDATENARTEN;
            der Kontenrahmen zeigt Konten *und* Kontengruppen, weil die
            Gruppe der Träger der Kontoeigenschaften ist (0019).
          */}
          {stammdatenarten(ort) && (
            <section>
              <h1>
                {ort.unterpunkt
                  ? unterpunktname(
                      bereich.unterpunkte.find((u) => u.id === ort.unterpunkt) ?? {
                        id: '',
                        schluessel: 'bereich.stammdaten.name',
                      },
                    )
                  : bereichsname(bereich)}
              </h1>
              {/*
                Über der Pflege der Baum — dieselbe Bauweise wie im
                Datenimport (R317): Die Übersicht wählt aus, gepflegt wird
                mit dem Bauteil, das der Anwender überall wiederfindet. Der
                Baum sagt zusätzlich etwas, das kein Blatt sagen kann: wie die
                Bilanz aussieht.
              */}
              {/*
                Derselbe Ort, dieselbe Bauweise: Über der Pflege der Baum
                (R317). Er sagt, woraus eine Gruppe besteht — und das sagt kein
                Eigenschaftsblatt, weil es an mehreren Zeilen zugleich hängt
                (2.4 Abschnitt 9).
              */}
              {/*
                Die Partnercodes (2.4 Abschnitt 10, R490–R493): Gruppierung und
                Reihenfolge hängen an mehreren Zeilen zugleich und stehen
                deshalb im Baum, nicht im Blatt — dieselbe Bauweise wie beim
                Ledgeraufbau (R317).
              */}
              {ort.bereich === 'stammdaten' && ort.unterpunkt === 'partnercodes' && (
                <Partnercodes
                  key={baumstand}
                  aufAuswahl={(schluessel) => setBaumwahl({ objektart: 'partnercode', schluessel })}
                  aufAenderung={() => setBaumwahl(null)}
                />
              )}
              {ort.bereich === 'stammdaten' && ort.unterpunkt === 'ledger' && (
                <Ledgeraufbau
                  key={baumstand}
                  aufAuswahl={(schluessel) => setBaumwahl({ objektart: 'ledger', schluessel })}
                  aufAenderung={() => setBaumwahl(null)}
                />
              )}

              {ort.bereich === 'stammdaten' && ort.unterpunkt === 'positionsplaene' && ledger && (
                <Positionsgliederung
                  key={baumstand}
                  ledger={ledger}
                  ledgerwahl={ledgerwahl}
                  aufLedger={setLedger}
                  periode={periode}
                  aufAuswahl={(schluessel) => setBaumwahl({ objektart: 'position', schluessel })}
                  aufAenderung={() => setBaumwahl(null)}
                />
              )}

              <Stammdaten
                zulaessig={stammdatenarten(ort)!}
                vorauswahl={baumwahl}
                aufAenderung={() => setBaumstand((n) => n + 1)}
                tafel={TAFELBEREICHE.has(`${ort.bereich}/${ort.unterpunkt ?? ''}`)}
                ohneAnlegen={BAUMANLAGE[`${ort.bereich}/${ort.unterpunkt ?? ''}`]}
                /*
                  **Wer seit wann zum Konzern gehört, steht im Blatt des
                  Kreises** (2.3, Auftrag vom 2026-09-26). Bis dahin war die
                  Kreiszugehörigkeit eine eigene Objektart in der Liste
                  links: Wer wissen wollte, wer dazugehört, wechselte den Ort
                  und filterte dort nach dem Kreis.
                */
                zusatz={(blatt) =>
                  blatt.objektart === 'konsolidierungskreis' ? (
                    <Kreismitglieder
                      key={blatt.schluessel}
                      kreis={blatt.schluessel}
                      aufAenderung={() => setBaumstand((n) => n + 1)}
                    />
                  ) : blatt.objektart === 'einheit' ? (
                    // Der erwartete Rhythmus der Datenlieferung, mit „gilt ab" (2.12 Abschnitt 3.27, R1523).
                    <Lieferrhythmen
                      key={blatt.schluessel}
                      einheit={blatt.schluessel}
                      aufAenderung={() => setBaumstand((n) => n + 1)}
                    />
                  ) : blatt.objektart === 'position' ? (
                    // Wo sie steht, mit jeder Fassung — seit die Gliederung
                    // keine eigene Liste mehr hat (R1453, R1454).
                    <Positionseinordnung key={`${blatt.schluessel}:${baumstand}`} position={blatt.schluessel} />
                  ) : blatt.objektart === 'konto' && !blatt.abWerk ? (
                    // Die Gruppe gehört zur Zuordnung — ab Monat oder immer (R1648, R1649).
                    <Kontogruppe
                      key={blatt.schluessel}
                      konto={blatt.schluessel}
                      periode={periode || undefined}
                      aufAenderung={() => setBaumstand((n) => n + 1)}
                    />
                  ) : null
                }
              />
            </section>
          )}

          {istPlatzhalter(ort) && (
            <Platzhalter bereich={bereich} unterpunkt={ort.unterpunkt} aufSprung={gehe} />
          )}

          {/*
            Über dem Bericht, nicht an seiner Stelle: Der Bericht bleibt stehen,
            das Fenster geht wieder zu, und der Anwender ist da, wo er war
            (R204). Der Verweis auf den Beleg wechselt dagegen wirklich den Ort
            — dann ist das Fenster zu (R209).
          */}
          {/*
            Die Buchungsmaske — dasselbe Fenster über dem Bericht wie der
            Wertenachweis (R385). Gebucht wird in den Eingang der Gesellschaft;
            wer in einen bestimmten Ordner buchen will, tut es im Journal.
          */}
          {buchung && (
            <Belegmaske
              vorbelegung={buchung}
              aufSchliessen={() => setBuchung(null)}
              aufGebucht={() => {
                setBuchung(null)
                /*
                  **Gebucht sagt das Kästchen, ohne Nummer** (7.2 Abschnitt
                  34, R1131). Der Bericht zeigt den Beleg erst mit dem nächsten
                  Lauf (R186) — das steht darunter, mit dem Weg ins Journal,
                  und das Kästchen bleibt, bis es geschlossen wird (R1132),
                  wie die Zeile, die bis zum 2026-10-06 hier stand.
                */
                meldeErfolg(t('erfolg.beleg.gebucht'), [
                  {
                    satz: t('erfolg.beleg.imjournal'),
                    knopf: t('beleg.menue.oeffnen'),
                    aufKnopf: () => {
                      setJournalsicht('ordner')
                      gehe('nachweis', 'buchungsjournal')
                    },
                  },
                ])
              }}
            />
          )}

          {/*
            **Anlegen am Bericht** (R494, 7.4 Abschnitt 14) — ein Fenster über
            dem Bericht wie die Buchungsmaske, und aus demselben Grund: Der
            Bericht bleibt stehen, das Fenster geht wieder zu, und der Anwender
            ist da, wo er war.

            **Der Bericht wird neu geladen, sobald etwas angelegt ist** (R569,
            berichtigt am 2026-09-16). Bis dahin stand hier das Gegenteil: Der
            Bericht lese festgeschriebene Ergebniszeilen, die Zeile komme mit
            dem nächsten Lauf (R498). Das war seit der **Laufvorsorge** (R294)
            nicht mehr wahr — sie holt den Lauf beim nächsten Abruf ohnehin
            nach. Der Satz beschrieb damit einen Zustand, den die Anwendung
            nicht mehr hatte, und der Anwender suchte eine Zeile, die da war.

            **Geladen wird nach dem Anlegen, nicht nach dem Schließen:** Der
            Anwender liest die Fertigmeldung, und in dieser Zeit rechnet und
            lädt der Bericht dahinter. Wer dann schließt, steht vor dem
            fertigen Bild — ohne Flackern und ohne Warten.
          */}
          {anlage && (
            <Strukturanlage
              ziel={anlage}
              aufAngelegt={() => setAnlagestand((n) => n + 1)}
              aufSchliessen={() => setAnlage(null)}
            />
          )}

          {/* Ein ausgeschnittenes Konto einfügen (7.3 Abschnitt 31, R1642) —
              einmal für jedes Berichtsbild; danach lädt der Bericht neu wie
              nach dem Anlegen. */}
          <Einfuegefenster periode={periode || null} aufEingefuegt={() => setAnlagestand((n) => n + 1)} />

          {/* Position löschen … und Konto löschen … im Bericht (2.1 Abschnitt
              3.6, R1705, R1707) — einmal für jedes Berichtsbild. */}
          <Elementloeschfenster aufGeloescht={() => setAnlagestand((n) => n + 1)} />

          {/*
            **Rechtsklick → Eigenschaften** (R858): das Blatt des Stammdatums,
            das in der Zeile steht. Wurde darin geschrieben, holt derselbe
            Stand wie nach dem Anlegen den Bericht neu.
          */}
          {reihenfolge && (
            <Reihenfolgefenster
              ziel={reihenfolge}
              aufSchliessen={(geaendert) => {
                setReihenfolge(null)
                if (geaendert) {
                  setAnlagestand((n) => n + 1)
                }
              }}
            />
          )}

          {eigenschaften && (
            <Eigenschaftsfenster
              ziel={eigenschaften}
              periode={periode || undefined}
              aufSchliessen={(geaendert) => {
                setEigenschaften(null)
                if (geaendert) {
                  setAnlagestand((n) => n + 1)
                }
              }}
            />
          )}

          {nachweis && (
            <Wertenachweis
              ziel={nachweis}
              aufSchliessen={() => setNachweis(null)}
              aufBeleg={(sprung) => {
                setNachweis(null)
                gehe('nachweis', 'buchungsjournal', {
                  lauf: sprung.lauf === null ? undefined : String(sprung.lauf),
                  vorgang: sprung.vorgangId === null ? undefined : String(sprung.vorgangId),
                  beleg: sprung.belegId === null ? undefined : String(sprung.belegId),
                  konto: sprung.konto ?? undefined,
                  einheit: sprung.einheit ?? undefined,
                })
              }}
            />
          )}

          {/*
            Derselbe Ort, dieselbe Bedienung, eine andere Auskunft: Der
            Nachweis aus der Kapitalflussrechnung erklärt eine Bewegung und
            keinen Bestand (2.8 Abschnitt 13.14). **Der Weg hinaus ist
            derselbe** — an die Belegzeile im Journal, nicht in eine Liste
            (R209).
          */}
          {kfrNachweis && (
            <Kfrwertenachweis
              ziel={kfrNachweis}
              aufSchliessen={() => setKfrNachweis(null)}
              aufBeleg={(sprung) => {
                setKfrNachweis(null)
                gehe('nachweis', 'buchungsjournal', {
                  lauf: sprung.lauf === null ? undefined : String(sprung.lauf),
                  vorgang: sprung.vorgangId === null ? undefined : String(sprung.vorgangId),
                  beleg: sprung.belegId === null ? undefined : String(sprung.belegId),
                  konto: sprung.konto ?? undefined,
                  einheit: sprung.einheit ?? undefined,
                })
              }}
            />
          )}
        </main>

        <footer className="zusagen">
          {/*
            Die Sprache ganz links unten. Sie stand oben rechts und hat dort
            Platz belegt, den der fachliche Kontext braucht. **Die
            Zahlendarstellung stand bis zum 2026-10-06 daneben** und steht
            jetzt im Profil (7.2 Abschnitt 43, R1314).
          */}
          <Schnellzugriff sprache={sprache} aufSprache={wechsleSprache} />

          <Zellsumme stand={zellstand} />

          {/*
            Änderungsnachweis und Antwortzeit standen bis zum 2026-09-11 hier,
            Lauf und Prüfcode bis zum 2026-10-02 (R868, 7.3 Abschnitt 21): Für
            den, der eine Bilanz liest, sind sie keine Auskunft über die Zahlen.
            Sie stehen im Konzernabschluss-Monitor, im Nachweis und in der Prüferansicht.
          */}
        </footer>
      </div>
    </div>
    </Rechtekontext>
    </Gliederungskontext>
    </Anzeigeoptionenkontext>
  )
}

/**
 * Jede Meldung nennt drei Dinge: was ist, wie viel es ist, was zu tun ist
 * (R145). Kein Code, keine Zeilennummer, kein technischer Text — auch nicht
 * als Zusatz. Eine unbekannte Ausnahme bekommt deshalb denselben Satz wie eine
 * Betriebsstörung: Was der Fachanwender nicht beheben kann, gehört an den
 * Betrieb, nicht auf seinen Bildschirm.
 */
/**
 * Eine stehende Meldung merkt sich **Schlüssel, keine Sätze.**
 *
 * Sonst bliebe eine Meldung, die vor dem Sprachwechsel entstanden ist, in der
 * alten Sprache stehen — und ausgerechnet der Anwender, der gerade auf seine
 * Sprache umstellt, liest den einen Satz weiter auf Deutsch, der ihm sagt,
 * dass etwas nicht geht.
 *
 * Der Rechenabbruch ist die Ausnahme: Sein Text kommt aus dem Rechenkern und
 * trägt dessen Sprache. Das ist die zweite Ebene von ADR 010 — fachliche
 * Texte aus dem Kern —, und sie ist noch nicht gebaut.
 */
type Meldung = { art: 'abbruch'; meldung: Textbaustein; handreichung: Textbaustein | null }

/**
 * Die erste Gesellschaft des Konzerns — die Rückfallebene der Buchungsmaske.
 *
 * **Geraten wird hier nichts.** Wer aus einer Zelle ohne Gesellschaft bucht
 * (der Konzernwert einer Position), bekommt eine eingestellte Gesellschaft,
 * die er sieht und wechseln kann — das ist ehrlicher als ein leeres Feld, das
 * ohne Rahmen keine Konten anbieten könnte.
 */
/** Ohne Abruf kennt keine Meldung ihren Konzernabschluss — sie nennt den Kreis (R1527). */
const KEINE_KONZERNABSCHLUESSE: Abschlusszeile[] = []

function ersteEinheit(): string {
  const [erste] = [...katalog().einheiten.values()].sort((a, b) => a.code.localeCompare(b.code))
  return erste?.schluessel ?? ''
}

/**
 * **Nur Fachliches wird zur Meldung im Arbeitsfeld** (7.2 Abschnitt 30,
 * R1004). Eine Störung steht im Störungsfenster und gibt hier `null` — so
 * räumt derselbe Aufruf auch eine alte Meldung weg.
 */
function alsMeldung(f: Error): Meldung | null {
  if (f instanceof Rechenabbruch) {
    return { art: 'abbruch', meldung: f.meldung, handreichung: f.handreichung }
  }

  if (f instanceof Fachbefund) {
    return { art: 'abbruch', meldung: { schluessel: f.schluessel, werte: [] }, handreichung: null }
  }

  // Störungen, die abgelaufene Sitzung und Programmfehler: Das Fenster sagt es.
  fachlicherSatz(f)
  return null
}

/**
 * Welche Objektarten ein Unterpunkt pflegt — oder `null`, wenn er keine
 * Stammdatenpflege ist (7.4, R235).
 *
 * **Der Unterpunkt ist die Adresse, die Objektart eine Auswahl darin** (R156).
 * Deshalb steht hier eine Liste je Unterpunkt und nicht eine Adresse je
 * Objektart: „Kontenrahmen" ist der Ort, an dem man Konten *und* Gruppen
 * pflegt — sie getrennt zu adressieren hieße, den Anwender zwischen zwei
 * Bildschirmen hin- und herzuschicken, um ein einziges geerbtes Feld zu
 * verstehen.
 */
function stammdatenarten(ort: Ort): string[] | null {
  return STAMMDATENARTEN[`${ort.bereich}/${ort.unterpunkt ?? ''}`] ?? null
}

/**
 * Wo der Bereich **als geschlossene Tabelle** über der Liste steht (7.4
 * Abschnitt 20, Auftrag vom 2026-09-19).
 *
 * **Das Bauteil ist objektartblind und könnte überall stehen.** Dass es hier
 * eine Liste von Adressen gibt und keinen Schalter „immer an", hat einen
 * Grund: Eine Tafel über achtzehn Beteiligungen beantwortet die Frage *„sieht
 * das überall gleich aus"*; eine Tafel über 41 Konten mit 13 Spalten
 * verdoppelt die Seitenhöhe, ohne eine Frage zu beantworten, die dort jemand
 * stellt (R610 — Was Nachschlagewerk ist, steht zugeklappt).
 *
 * **Die Konzernstruktur ist der Fall, für den sie gebaut ist:** Wer eine
 * Mutter hat, pflegt dieselbe Beteiligungsart achtzehnmal — oder einmal, mit
 * einem Griff über die Spalte.
 */
const TAFELBEREICHE = new Set([
  'konzernstruktur/beteiligungsregister',
  'konzernstruktur/kreise',
])

/**
 * **Wo ein Baum anlegt, legt das Blatt nicht an** (R701).
 *
 * Je Adresse die Objektarten, deren Anlage oben im Baum stattfindet und unten
 * im Blatt deshalb keinen zweiten Knopf bekommt. Ein Partnercode, eine
 * Bewertungsebene und eine Position entstehen **an ihrer Stelle** — unter
 * einer Gruppe, in einer Reihenfolge; beides weiß nur der Baum. Der
 * allgemeine Knopf *Neu anlegen* legte sie stattdessen stumm an den Anfang,
 * und der Anwender hatte zwei Knöpfe nebeneinander, die gleich hießen und
 * verschiedene Dinge taten.
 *
 * **Genannt sind Objektarten und nicht Bereiche**, weil ein Unterpunkt
 * mehrere führt: Unter *Positionspläne* steht neben der Position auch der
 * **Plan** — und der entsteht nur hier, denn ein Baum, den es noch nicht
 * gibt, kann ihn nicht anlegen. Dasselbe gilt für die Freigabe einer
 * Gliederung und für die Kontozuordnung.
 *
 * **Die Entscheidung steht bei den Adressen** und nicht im Bauteil — wie bei
 * TAFELBEREICHE darüber, und aus demselben Grund: Sie gilt einem Bildschirm.
 */
const BAUMANLAGE: Record<string, string[]> = {
  // Ein Konto entsteht nur unter einer Position (7.4 Abschnitt 34.12, R1601).
  'stammdaten/kontenrahmen': ['konto'],
  'stammdaten/partnercodes': ['partnercode'],
  'stammdaten/ledger': ['ledger'],
  'stammdaten/positionsplaene': ['position'],
}

/**
 * Alles, wofür es noch keinen gebauten Bildschirm gibt.
 *
 * **Die Antwort steht in `bereiche.tsx` und wird hier nur gelesen.** Bis zum
 * 2026-09-10 stand hier eine zweite Liste der gebauten Bildschirme neben dem
 * `gebaut`-Merkmal der Unterpunkte — und beim ersten neuen Bildschirm ist sie
 * prompt auseinandergelaufen: Die Kapitalflussrechnung war in der Leiste
 * fertig und bekam trotzdem den Platzhalter untergeschoben. Zwei Listen für
 * dieselbe Aussage gehen mit der Zeit immer auseinander; eine kann es nicht.
 */
function istPlatzhalter(ort: Ort): boolean {
  if (stammdatenarten(ort) || ort.bereich === 'arbeitsliste') {
    return false
  }

  const unterpunkt = findeBereich(ort.bereich).unterpunkte.find((u) => u.id === ort.unterpunkt)
  return !unterpunkt?.gebaut
}

/** Ob zu jeder Warnung ein Satz dasteht (R912) — ein Hinweis braucht keinen (R1377). */
function alleBegruendet(urteil: Freigabeurteil, begruendungen: Record<string, string>): boolean {
  return urteil.hinweise
    .filter((h) => verlangtSatz(urteil, h.code))
    .every((h) => (begruendungen[h.code] ?? '').trim().length > 0)
}

/** Die Prüfungen, die für diese Periode auf „Nicht prüfen" stehen (R913). */
function ungeprueft(urteil: Freigabeurteil): string[] {
  return Object.entries(urteil.stufen ?? {})
    .filter(([, stufe]) => stufe === 'aus')
    .map(([code]) => code)
}

function Konzernabschlussmonitor({
  lauf,
  urteil,
  gibtFrei,
  aufFreigabe,
  kontext,
  aufSprung,
  aufGeaendert,
}: {
  lauf: Rechenantwort | null
  urteil: Freigabeurteil | null
  gibtFrei: boolean
  aufFreigabe: (begruendungen: Record<string, string>) => void
  /** Nach einem Handgriff am Schloss: Lauf und Urteil neu holen (R1690–R1693). */
  aufGeaendert: () => void
  /** Der Konzernabschluss, um den es geht — für Überschrift und Verweis der Befunde (R1527). */
  kontext: Meldungskontext
  aufSprung: (bereich: string, unterpunkt?: string, auswahl?: Record<string, string>) => void
}) {
  /*
    **Dieselben Sätze wie in der Arbeitsliste** (7.1 Abschnitt 13, R1527):
    Überschrift, Satz mit Verweis, Ziel — aus einer Stelle. Bis zum
    2026-10-10 stand hier der Satz ohne Verweis.
  */
  const befundblock = (befund: Freigabebefund) => {
    const { was, weg, ziel } = befundtexte(befund, kontext)
    return (
      <>
        <div>{was}</div>
        {weg && (
          <div className="erlaeuterung">
            <Verweissatz text={weg} aufVerweis={() => aufSprung(ziel.bereich, ziel.unterpunkt, ziel.auswahl)} />
          </div>
        )}
      </>
    )
  }
  // Je Hinweis ein Satz (R912) — gemerkt, solange der Monitor offen ist.
  const [begruendungen, setBegruendungen] = useState<Record<string, string>>({})
  const darf = useDarf()

  if (!lauf) {
    return (
      <section>
        <h1>{t('bereich.abschluss.monitor')}</h1>
        {/* Datenimporte, die eine Aktualisierung brauchen (R880) — auch ohne Lauf. */}
        <Aktualisierungswarnung />
        <p className="hinweis">{t('monitor.hinweis')}</p>
      </section>
    )
  }

  return (
    <section>
      <h1>{t('bereich.abschluss.monitor')}</h1>

      {/*
        **Die Warnung steht über dem Lauf** (5.1 Abschnitt 3.36, R880): Sie
        sagt, ob die Zahlen darunter auf aktuellem Stand gerechnet sind.
      */}
      <Aktualisierungswarnung />

      {/*
        Die drei Zusagen sind absichtlich nicht versteckt. Der Prüfcode steht
        neben dem Ergebnis; die gemessene Antwortzeit und die Unversehrtheit
        des Änderungsnachweises stehen seit dem 2026-09-11 im Betrieb unter
        „Systemzustand" — nicht mehr in der Fußzeile, wo sie jedem Anwender
        bei jeder Bilanz mitliefen. Eine Zusage, die man erst in einem
        Betriebshandbuch findet, überzeugt niemanden; eine, die niemanden
        angeht, der gerade liest, lenkt ab.
      */}
      <div className="kennzahlen">
        {/*
          **Der Lauf heißt nach seinem Zeitpunkt** (7.2 Abschnitt 36, R1142).
          Bis zum 2026-10-06 standen hier Laufnummer, Datenstand (die Nummer
          einer Erfassung), Vorgänge, Buchungen und Rechenzeit in
          Millisekunden — Nummern und Mengen ohne Entscheidungswert (R1141,
          R1143). Die Ausgabe des Regelwerks ist ein Name, den der Anwender
          vergibt, und bleibt; der Prüfcode ist der Fingerabdruck der Rechnung.
        */}
        <Kennzahl name={t('monitor.lauf')}>{zeitpunkt(lauf.gerechnetAm)}</Kennzahl>
        <Kennzahl name={t('monitor.waehrung')}>{lauf.konzernwaehrung}</Kennzahl>
        <Kennzahl name={t('monitor.regelwerk')}>
          {t('monitor.ausgabe', { nummer: String(lauf.regelwerksausgabe) })}
        </Kennzahl>
        <Kennzahl name={t('monitor.pruefcode')}>
          <span className="hash">{lauf.ergebnisHash}</span>
        </Kennzahl>
      </div>

      {lauf.hinweise.length > 0 && (
        <div className="meldung">
          <h2>{t('monitor.hinweise')}</h2>
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {lauf.hinweise.map((h, i) => (
              <li key={i}>{satz(h)}</li>
            ))}
          </ul>
        </div>
      )}

      {/*
        Rot heißt „hier stimmt etwas nicht". Ein freigegebener Abschluss ist
        aber der gewünschte Endzustand und kein Mangel — er ist nur nicht noch
        einmal freigebbar. Gesperrt ist deshalb allein, was noch offen ist und
        trotzdem nicht durchkommt.
      */}
      {urteil && (
        <div
          className={`freigabe ${
            istOffen(urteil.abschlussstatus) && !urteil.freigabefaehig ? 'gesperrt' : 'moeglich'
          }`}
        >
          <div className="zeile">
            <span>
              <span
                className={`ampel ${
                  istOffen(urteil.abschlussstatus) && !urteil.freigabefaehig ? 'schlecht' : 'gut'
                }`}
              />
              <strong>
                {istOffen(urteil.abschlussstatus)
                  ? t(urteil.freigabefaehig ? 'freigabe.moeglich' : 'freigabe.gesperrt')
                  : t('freigabe.zustand', { status: abschlussstatus(urteil.abschlussstatus) })}
              </strong>{' '}
              {/*
                **Geprüft seit Beginn des Geschäftsjahres** (2.13 Abschnitt 19,
                R1522): Wer sieht, dass der März ohne Befund ist, der Januar
                aber gesperrt war, liest hier den Grund.
              */}
              {urteil.geprueftVon != null && (
                <span className="gedaempft">
                  {t('freigabe.zeitraum', {
                    von: periodenname(urteil.geprueftVon),
                    bis: periodenname(urteil.periode),
                  })}
                </span>
              )}
            </span>

            {/*
              Der Zustand muss mitentscheiden, nicht nur die Freigabefähigkeit:
              Eine gelungene Freigabe antwortet mit „freigabefähig, keine
              Befunde" — daran allein aufgehängt, bliebe der Knopf danach stehen
              und lüde zum zweiten Mal ein.
            */}
            {darf('freigeben') && urteil.freigabefaehig && istOffen(urteil.abschlussstatus) && (
              <button
                className="haupt"
                onClick={() => aufFreigabe(begruendungen)}
                disabled={gibtFrei || !alleBegruendet(urteil, begruendungen)}
              >
                {t(gibtFrei ? 'freigabe.laeuft' : 'freigabe.knopf')}
              </button>
            )}
          </div>

          {/*
            Was sperrt, sperrt (R910). Es gibt hier deshalb bewusst keinen
            Knopf „trotzdem freigeben" — wie streng eine Prüfung wiegt, legt
            der Konzern auf der Seite *Prüfregeln festlegen* fest, nicht der
            Freigeber im Augenblick, in dem sie stört.
          */}
          {urteil.befunde.map((befund) => (
            <div key={befund.code} className="befund">
              {befundblock(befund)}
            </div>
          ))}

          {/*
            **Wer trotz Warnungen freigibt, begründet jede einzeln** (R912).
            Der Satz steht danach neben dem Ergebnis — unter „Übergangene
            Prüfungen" im Nachweis und beim Prüfer. Ein Hinweis steht ohne
            Feld da: Er geht kommentarlos durch (R1377).
          */}
          {istOffen(urteil.abschlussstatus) &&
            urteil.hinweise.map((hinweis) => (
              <div key={hinweis.code} className="befund hinweis">
                {befundblock(hinweis)}
                {darf('freigeben') && urteil.freigabefaehig && verlangtSatz(urteil, hinweis.code) && (
                  <label className="feld">
                    <span>{t('freigabe.begruendung')}</span>
                    <textarea
                      rows={2}
                      value={begruendungen[hinweis.code] ?? ''}
                      onChange={(e) => setBegruendungen((b) => ({ ...b, [hinweis.code]: e.target.value }))}
                    />
                  </label>
                )}
              </div>
            ))}

          {/* Was nicht geprüft wird, steht da (R913) — eine Prüfung, die fehlt, sähe aus wie eine bestandene. */}
          {istOffen(urteil.abschlussstatus) && ungeprueft(urteil).length > 0 && (
            <p className="erlaeuterung">
              {t('freigabe.ungeprueft', {
                liste: ungeprueft(urteil)
                  .map((code) => t(`pruefung.${code}` as Sprachschluessel))
                  .join(', '),
              })}
            </p>
          )}
        </div>
      )}

      {/*
        **Die Handgriffe am Schloss** (2.13 Abschnitt 20.7, R1690–R1694):
        schließen, wiedereröffnen, Freigabe zurücknehmen — hier und nur hier
        (R1609). Gerechnet wird am Konzernabschluss.
      */}
      <Schloss
        kreis={lauf.kreis}
        ledger={lauf.ledger}
        periode={lauf.periode}
        freigabestatus={urteil?.abschlussstatus ?? null}
        aufGeaendert={aufGeaendert}
      />
    </section>
  )
}

/**
 * Die Gesellschaft im Partnerbild — ein Feld in der Berichtsleiste, neben
 * der Spaltenwahl und der Gliederung: alle drei entscheiden, wie derselbe
 * Lauf gelesen wird. „Alle" ist der Kreis; eine gewählte Gesellschaft zeigt
 * nur ihre Handelsbilanz II je Partner (7.3 Abschnitt 4.4).
 */
function Gesellschaftswahl({
  einheiten,
  gewaehlt,
  aufWahl,
}: {
  einheiten: string[]
  gewaehlt: string
  aufWahl: (e: string) => void
}) {
  const nach = katalog()

  const wahl = useMemo(
    () =>
      einheiten.map((e) => {
        const eintrag = nach.einheiten.get(e)
        const name = eintrag ? beschriftung(eintrag.namen) : ''
        return {
          wert: e,
          text: `${eintrag?.code ?? e}${name ? ` · ${name}` : ''}`,
          suchtext: [e, ...Object.values(eintrag?.namen ?? {})].join(' '),
        }
      }),
    [einheiten, nach],
  )

  // Steht die gemerkte Gesellschaft nicht mehr im Kreis, gilt wieder der ganze Kreis (R1364).
  useEffect(() => {
    if (gewaehlt && einheiten.length > 0 && !einheiten.includes(gewaehlt)) {
      aufWahl('')
    }
  }, [einheiten, gewaehlt, aufWahl])

  return (
    <div className="feld gesellschaftswahl">
      <label htmlFor="gesellschaft">{t('spalten.gesellschaft')}</label>
      <Auswahlfeld
        id="gesellschaft"
        wert={gewaehlt}
        eintraege={wahl}
        aufWahl={aufWahl}
        leer={t('spalten.gesellschaft.alle')}
      />
    </div>
  )
}

/**
 * Was steht, wenn nichts da ist (R294).
 *
 * **Der Satz hat sich am 2026-09-10 umgedreht.** Vorher hieß er „noch nicht
 * gerechnet" und führte an einen Knopf; das war eine Auskunft über den
 * Bedienzustand der Anwendung. Fehlende Läufe holt die Vorsorge jetzt selbst
 * nach — bleibt der Bildschirm trotzdem leer, liegt es an den Daten und nicht
 * an einem ungedrückten Knopf. Deshalb auch kein Sprungziel mehr: Der Weg
 * führt zurück in die Kontextzeile, nicht in einen anderen Bereich.
 */
function KeineDaten({ konzern = false }: { konzern?: boolean }) {
  // **Ohne Konzernabschluss keine Konzernzahlen** (2.13 Abschnitt 14, R1112):
  // Für einen Kreis heißt ein leerer Bericht, dass kein Konzernabschluss
  // gerechnet ist — der Weg dorthin steht in der Kopfzeile darüber.
  if (konzern) {
    return (
      <div className="meldung neutral">
        <h2>{t('keinedaten.konzern.titel')}</h2>
        <p>{t('keinedaten.konzern.text')}</p>
        <p className="handreichung">{t('keinedaten.konzern.weg')}</p>
      </div>
    )
  }

  return (
    <div className="meldung neutral">
      <h2>{t('keinedaten.titel')}</h2>
      <p>{t('keinedaten.text')}</p>
      <p className="handreichung">{t('keinedaten.weg')}</p>
    </div>
  )
}

function Kennzahl({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="kennzahl">
      <span className="name">{name}</span>
      <span className="wert">{children}</span>
    </div>
  )
}

/**
 * Ist der Abschluss noch veränderlich?
 *
 * Ab „freigegeben" ist er ein Snapshot (K3) und wird nicht wiedereröffnet;
 * „testiert" beendet zusätzlich die Neu-Rechenbarkeit (R101a).
 */
function istOffen(status: string): boolean {
  return status === 'offen' || status === 'in_arbeit'
}
